﻿/**
 * English locale messages
 * This is the source of truth for all translatable strings in the app.
 * All other locale files (es, fr, de, it) mirror this structure.
 */

import { brand } from '../config/brand';

export default {
  // Weather controls + modal (CurrentWeather, WeatherModal, WeatherForecast)
  weather: {
    title: 'Current Weather',
    refresh: 'Refresh weather',
    updated: 'Updated',
    justNow: 'just now',
    feelsLike: 'Feels like',
    humidity: 'Humidity',
    wind: 'Wind',
    pressure: 'Pressure',
    clouds: 'Clouds',
    visibility: 'Visibility',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    hourlyForecast: 'Hourly forecast',
    dailyForecast: 'Daily forecast',
    forecastTitle: 'Weather Forecast',
    hourly: 'Hourly',
    daily: 'Daily',
    today: 'Today',
    tomorrow: 'Tomorrow',
    unavailable: 'Weather data is not available yet.',
    forecastUnavailable: 'Forecast not available.',
    rainPercent: '{count}% rain',
    noRain: 'No rain',
    configureInSettings: 'Configure weather in Settings',
    loadFailed: 'Failed to load weather data',
    alertsTitle: 'Weather Alerts',
    now: 'Now',
    scrub: 'Scrub through the forecast',
    sun: 'Sun',
    moon: 'Moon',
    moonrise: 'Moonrise',
    moonset: 'Moonset',
    illumination: 'lit',
    details: 'Details',
  },
  // Add Room wizard (AddRoomModal) - static UI strings
  roomWizard: {
    addRooms: 'Add Rooms',
    buildingType: 'Building Type',
    selectDefaults: 'Select Default Rooms for {type}',
    selectCommonRooms: 'Select Common Rooms',
    alreadyExists: 'Already exists',
    floor: 'Floor {number}',
    addCustomRoom: 'Add Custom Room',
    roomName: 'Room Name',
    roomNamePlaceholder: 'e.g., Guest Bedroom, Wine Cellar',
    floorNumber: 'Floor Number',
    floorNumberPlaceholder: '0, 1, 2...',
    addCustomRoomButton: 'Add Custom Room to Selection',
    areaAssignment: 'Area Assignment (Optional)',
    assignToArea: 'Assign to Area',
    noArea: 'No Area',
    areaHelp: 'All selected rooms will be assigned to this area',
    selectedRooms: 'Selected Rooms ({count})',
    clearAll: 'Clear All',
    cancel: 'Cancel',
    creating: 'Creating...',
    createOne: 'Create {count} Room',
    createMany: 'Create {count} Rooms',
    searchIcons: 'Search icons',
  },
  // Add Room wizard - building types and common room presets
  roomPresets: {
    buildingTypes: {
      allRooms: 'All Rooms',
      home: 'Home',
      house: 'House',
      office: 'Office',
      school: 'School',
      hotel: 'Hotel',
      hospital: 'Hospital',
    },
    rooms: {
      entrance_hall: 'Entrance Hall',
      living_room: 'Living Room',
      dining_room: 'Dining Room',
      kitchen: 'Kitchen',
      pantry: 'Pantry',
      family_room: 'Family Room',
      study: 'Study',
      home_office: 'Home Office',
      library: 'Library',
      master_bedroom: 'Master Bedroom',
      bedroom: 'Bedroom',
      bedroom_2: 'Bedroom 2',
      bedroom_3: 'Bedroom 3',
      bedroom_4: 'Bedroom 4',
      guest_bedroom: 'Guest Bedroom',
      kids_room: 'Kids Room',
      master_bathroom: 'Master Bathroom',
      bathroom: 'Bathroom',
      powder_room: 'Powder Room',
      laundry: 'Laundry Room',
      garage: 'Garage',
      workshop: 'Workshop',
      storage: 'Storage Room',
      utility_room: 'Utility Room',
      basement: 'Basement',
      wine_cellar: 'Wine Cellar',
      home_theater: 'Home Theater',
      game_room: 'Game Room',
      gym: 'Gym',
      patio: 'Patio',
      balcony: 'Balcony',
      terrace: 'Terrace',
      garden: 'Garden',
      pool_area: 'Pool Area',
      hallway: 'Hallway',
      attic: 'Attic',
      reception: 'Reception',
      office: 'Office',
      meeting_room: 'Meeting Room',
      conference_room: 'Conference Room',
      break_room: 'Break Room',
      restroom: 'Restroom',
      server_room: 'Server Room',
      classroom: 'Classroom',
      cafeteria: 'Cafeteria',
      gymnasium: 'Gymnasium',
      laboratory: 'Laboratory',
      playground: 'Playground',
      lobby: 'Lobby',
      guest_room: 'Guest Room',
      suite: 'Suite',
      restaurant: 'Restaurant',
    },
  },
  // ──────────────────────────────────────────────────────────────────────────
  // Common UI elements - buttons, actions, generic labels
  // ──────────────────────────────────────────────────────────────────────────
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    remove: 'Remove',
    close: 'Close',
    back: 'Back',
    confirm: 'Confirm',
    ok: 'OK',
    loading: 'Loading...',
    saving: 'Saving...',
    error: 'Error',
    more: 'More',
    copy: 'Copy',
    all: 'All',
    language: 'Language',
  },

  // Dialog / message-box strings (confirm, alert, toast)
  dialogs: {
    // Generic
    areYouSure: 'Are you sure?',
    typeDelete: 'Type "DELETE"',
    finalWarning: 'FINAL WARNING. All data deleted. Continue?',
    done: 'Done',
    errorWithMessage: 'Error: {message}',
    saveFailed: 'Failed to save changes',
    validationErrors: 'Please fix the following errors:\n{errors}',
    deleteAllConversationHistory: 'Are you sure you want to delete all conversation history? This cannot be undone.',
    unregisterFromCloud: 'Unregister from cloud? Remote access will be disabled.',
    removeUserAccess: "Remove this user's access?",
    allDevicesDeleted: 'All devices deleted',
    allRoomsDeleted: 'All rooms deleted',
    allIntegrationsDeleted: 'All integrations deleted',
    allScenesDeleted: 'All scenes deleted',
    factoryResetInitiated: 'Factory reset initiated.',

    // Device
    deviceDeleteConfirm: 'Are you sure you want to delete "{name}"?\n\nThis action cannot be undone.',
    deviceDeleteConfirmShort: 'Are you sure you want to delete this device?',
    deviceRemoveConfirm: 'Are you sure you want to remove this device?',
    deviceDeleteFailed: 'Failed to delete device',
    devicesDeleteFailed: 'Failed to delete devices',
    deviceDeleteFailedMessage: 'Failed to delete device: {message}',
    deviceDuplicateFailed: 'Failed to duplicate device: {message}',
    deviceReorderFailed: 'Failed to update device order',
    deviceReinitConfirm:
      'This will delete and recreate all device values from the device type definition. User-created values will be preserved. References (KNX addresses, Matter mappings) will be lost and must be re-configured. Continue?',
    deviceReinitFailed: 'Failed to reinitialize device values',
    deviceRefsSaveFailed: 'Failed to save references',
    deviceRefsUpdateFailed: 'Some references failed to update:\n{errors}',
    customValueDeleteConfirm: 'Delete custom value "{name}"?',
    thumbnailUploadFailed: 'Device thumbnail upload failed: {message}',
    thumbnailRemoveFailed: 'Device thumbnail removal failed: {message}',
    stateThumbnailUploadFailed: 'State image upload failed: {message}',
    stateThumbnailRemoveFailed: 'State image removal failed: {message}',
    deviceRoomUpdateFailed: 'Failed to update room: {message}',

    // Widget / dashboard
    widgetDeleteConfirm: 'Are you sure you want to delete this widget?',
    widgetMissing: 'This widget no longer exists. It may have been removed. The widget list will refresh.',
    widgetSaveFailed: 'Save failed: {message}',
    widgetRemoveConfirm: 'Remove this widget from the dashboard?',
    dashboardRebuildConfirm: 'Rebuild "{name}" as {label}? Existing widgets on this page will be replaced.',
    dashboardDeleteConfirm: 'Delete "{name}"? This cannot be undone.',
    dashboardGroupDeleteConfirm: 'Delete group "{name}"? Pages in this group will become ungrouped.',
    dashboardGroupDeleteCountConfirm: 'Delete group "{name}"? {count} page(s) will become ungrouped.',

    // Room
    roomExistsInDb: 'Room "{name}" already exists in the database.',
    roomAlreadyInSelection: 'Room "{name}" is already in your selection.',
    roomsCreateFailed: 'Failed to create rooms',
    roomUpdateFailed: 'Failed to update room',

    // Scene
    sceneCreateFailed: 'Failed to create scene',
    sceneDeleteFailed: 'Failed to delete scene',
    sceneValuesSaveFailed: 'Failed to save scene values',
    sceneThumbnailUploadFailed: 'Failed to upload thumbnail',

    // Automation
    automationCreateFailed: 'Failed to create function',
    automationSaveFailed: 'Failed to save function',
    automationDeleteConfirm: 'Are you sure you want to delete "{name}"?',
    automationDeleteFailed: 'Failed to delete function',
    automationDeleteFailedRetry: 'Failed to delete function. Please try again.',

    // Calendar
    calendarDeleteConfirm: 'Delete "{name}" and all its events?',
    calendarSaveChanges: 'Do you want to save your changes?',
    calendarDiscardChanges: 'Discard unsaved changes?',
    calendarDeleteEvent: 'Delete "{title}"?',

    // Camera / integration
    cameraDeleteRecording: 'Delete this recording? All segments will be permanently removed.',
    cameraDeleteRecordingEvent: 'Delete this recording event? All segments will be permanently deleted.',
    cameraDeleteConfirm: 'Delete camera "{name}"? All recordings will be permanently deleted.',
    cameraDeleteAllRecordings: 'Delete ALL recordings for "{name}"? This cannot be undone.',
    cameraResetSettings: 'Reset all streaming settings to defaults?',
    cameraToggleFailed: 'Failed to toggle integration: {message}',
    cameraNameSaveFailed: 'Failed to save name: {message}',
    cameraSettingsSaved: 'Camera settings saved successfully',
    cameraSettingsSaveFailed: 'Failed to save camera settings: {message}',
    integrationConfigSaveFailed: 'Failed to save config: {message}',
    knxNoInterfaces:
      'No KNX IP interfaces found on the network. Make sure your KNX IP interface is powered on and connected.',
    discoveryFailed: 'Discovery failed: {message}',
    devicesAddFailed: 'Failed to add some devices: {message}',

    // Gallery / images
    imageDeleteConfirm: 'Are you sure you want to delete this image?',
    imageDeleteForEntity: 'Delete {type} image for {entity}?',
    imageReplaceForEntity: 'Replace {type} image for {entity}?',
    fileTooLarge: 'File is too large (max 50MB)',
    fileDeleteConfirm: 'Delete "{name}"?',
    fileDeleteFailed: 'Delete failed',
    albumAddFailed: 'Failed to add to album',
    albumRemoveFailed: 'Failed to remove from album',
    albumSaveFailed: 'Failed to save album',
    albumDeleteConfirm: 'Delete album "{name}"? Photos will not be deleted.',
    albumDeleteFailed: 'Failed to delete album',

    // Media / notes
    mediaDeleteConfirm: 'Delete "{title}" from the media library? This also removes the file from the server.',
    playlistDeleteConfirm: 'Delete this playlist?',
    zoneDeleteConfirm: 'Delete this zone? This will stop playback and remove all members.',
    mediaPlayerRemoveConfirm: 'Remove this media player?',
    coverPhotoUploadFailed: 'Failed to upload cover photo',
    coverPhotoRemoveConfirm: 'Remove this cover photo?',
    coverPhotoRemoveFailed: 'Failed to remove cover photo',
    noteSaveFailed: 'Failed to save note',
    noteDeleteConfirm: 'Delete this note and all its images?',
    noteDeleteFailed: 'Failed to delete note',

    // Agent
    agentClearConversation: 'Clear this conversation?',
    logoutConfirm: 'Are you sure you want to log out?',
    reminderDeleteConfirm: 'Delete this reminder?',

    // Shopping
    shoppingListSaveFailed: 'Failed to save list',
    shoppingListDeleteConfirm: 'Delete this list and all its items?',
    shoppingListDeleteFailed: 'Failed to delete list',
    shoppingItemAddFailed: 'Failed to add item',
    shoppingClearCheckedConfirm: 'Remove all checked items?',
    shoppingClearFailed: 'Failed to clear items',
    shoppingStoreAddFailed: 'Failed to add store',
    shoppingStoreDeleteConfirm: 'Delete this store? Lists will keep but lose store association.',
    shoppingStoreDeleteFailed: 'Failed to delete store',

    // Integration (Matter / Z-Wave / Zigbee / monitoring)
    monitoringStartFailed: 'Failed to start monitoring: {message}',
    monitorWriteFailed: 'Failed to write: {message}',
    monitorReadFailed: 'Failed to read: {message}',
    mqttPropertyDeleteConfirm: 'Delete this MQTT property? Existing device references will be removed.',
    modbusRegisterDeleteConfirm: 'Delete this Modbus register? Existing device references will be removed.',
    knxDatapointDeleteConfirm: 'Delete this KNX group address? Existing device references will be removed.',
    commissioningWindowFailed: 'Failed to open commissioning window',
    matterDeviceUpdateFailed: 'Failed to update device',
    matterDeviceSaveFailed: 'Failed to save device',
    matterDeleteFailed: 'Failed to delete',
    matterError: '❌ Error: {message}',
    matterReinterviewConfirm: 'Re-interview node {id}? This will delete and recreate all devices for this node.',
    matterDecommissioned: '✅ Matter node successfully decommissioned and deleted',
    matterNodeDeleteError: '❌ Error deleting node: {message}',
    matterNodeRemoveTitle: 'Remove Matter node',
    matterNodeRemoveInspecting: 'Inspecting the node…',
    matterNodeRemoveDecommission: 'The controller still holds this node — it will be decommissioned first, then its rows removed.',
    matterNodeRemoveGhost: 'The controller does not have this node (its storage was reset, or the node was commissioned into another fabric). Its rows will be pruned without contacting the controller.',
    matterNodeRemovePruneCounts: 'Rows to remove: {endpoints} endpoint(s), {clusters} cluster(s), {properties} attribute row(s).',
    matterNodeRemoveControllerOffline: 'The Matter controller is not running, so removal cannot decide between decommission and prune. Start this integration first — the hub refuses to delete rows it cannot check.',
    matterNodeRemoveDevicesFlag: 'Also delete the {count} device(s) created from this node (listed below)',
    matterNodeRemoveDetach: '{count} device(s) from another integration are only detached from this node, not deleted.',
    matterNodeRemoveOtherIntegration: 'other integration',
    matterNodeRemoveConfirm: 'Remove node',
    matterNodeRemoving: 'Removing…',
    matterNodeRemoved: '✅ Node removed — {endpoints} endpoints, {clusters} clusters, {properties} attribute rows, {devices} device(s).',
    matterNodeGhostHint: 'Not in the Matter controller — this node can never be commanded.',
    matterNodeRemoveAction: 'Remove',
    matterDiagnosticsFailed: 'Failed to get diagnostics: {message}',
    matterFabricResetConfirm:
      '⚠️ Reset Matter Fabric?\n\nThis will:\n- Delete all Matter storage and credentials\n- Generate a new Fabric ID\n- Remove all commissioned devices\n- Require re-commissioning all devices\n\nThis action CANNOT be undone. Continue?',
    matterFabricResetSuccess:
      '✅ Fabric reset successful!\n\nThe Matter controller will restart with a new Fabric ID.\nPlease refresh the page in a few seconds.',
    matterFabricResetFailed: '❌ Failed to reset fabric: {message}',
    factoryResetFailed: 'Factory reset failed',
    matterDeviceRemoveFailed: 'Failed to remove device',
    threadSaveFailed: 'Failed to save Thread dataset. The feature might not be implemented on the hub yet.',
    zigbeeGroupDeleteConfirm: 'Delete group {id}?',
    remoteSupportEndSessionFailed: 'Failed to end session',
    remoteSupportSendMessageFailed: 'Failed to send message',
    zigbeeNodeRemoveConfirm: 'Remove node {id}?',
    zwaveNodeRemoveConfirm: 'Remove node {id} from the database?',
    zwaveNodeRenameFailed: 'Failed to rename node',
    zwaveEndpointRenameFailed: 'Failed to rename endpoint',
    wifiSaveFailed: 'Failed to save WiFi credentials. The feature might not be implemented on the hub yet.',
    touchPanelUnpairConfirm: 'Unpair "{name}"? The device will need to be re-paired.',
    mapZoneDeleteConfirm: 'Delete zone "{name}"?',
    securityPinRemoveConfirm: 'Remove the secret devices PIN? Secret cameras will become accessible without a PIN.',

    // Settings
    scheduleEditorComingSoon: 'Schedule editor coming soon. Configure device schedules via the API or Energy page.',
    libraryDeleteConfirm: 'Delete this item?',
    securityModeDeleteConfirm: 'Delete this security mode? This cannot be undone.',
    remoteSupportRemoveUserFailed: 'Failed to remove user',
    remoteSupportUpdateAccessFailed: 'Failed to update access',

    // User
    userSaveFailed: 'Failed to save user',
    userDeleteFailed: 'Failed to delete user: {message}',
    profilePictureRemoveConfirm: 'Are you sure you want to remove your profile picture?',

    // Servers
    serverRemoveFromCloud: `Remove "{hub}" from ${brand.agentName} Cloud?\n\nWarnings:\n- Shared access entries for this server will be deleted.\n- The local ${brand.agentName} server will keep running.\n- To reappear in cloud, the server must reconnect and register again.\n\nThis action cannot be undone.`,

    // Editor
    imageUrlMissing: 'Upload succeeded but no image URL was returned.',
    imageUploadFailed: 'Failed to upload image',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Authentication
  // ──────────────────────────────────────────────────────────────────────────
  auth: {
    signIn: 'Sign In',
    emailAddress: 'Email Address',
    passwordPlaceholder: 'Enter your password',
    signInToYourHub: 'Sign in to your hub',
    signInToOttoHome: `Sign in to ${brand.platformCompact}`,
    signingIn: 'Signing in...',
    invalidCredentials: 'Invalid email or password',
    // No "Forgot password?" link on the sign-in form: the hub serves no
    // forgot/reset route any more (PLAN.md §4 phase 4, hub commit 51677c7),
    // so this sentence is the whole answer the form can honestly give.
    noPasswordReset:
      'There is no password reset: this hub grants access by invite, not by email. Ask an administrator to invite you, or connect this device from the app.',
    // The demo house's guest door: a way to see the product for anyone who has
    // nothing to sign in with yet — no account, just a name.
    tryLiveDemo: 'Step into the live demo',
    // The hub refused the device credential this installation held (revoked, or
    // its binding expired): `stores/auth.ts` → `notifyDeviceCredentialRejected`
    // says so once, and the way back is a sign-in — an account or this hub's own
    // local password. There is no code to scan any more.
    credentialRejected:
      'This hub no longer accepts this device’s connection. Sign in again — with your {platform} account, or with this hub’s local password — to connect it again.',
    genericError: 'An error occurred. Please try again.',
    secureSmartHome: 'Secure access to your smart home',
    usernameOrEmail: 'Username or Email',
    setupSystemNamePlaceholder: 'e.g. My Home',
    setupUploadImage: 'Upload image',
    setupNext: 'Next',
    setupBack: 'Back',
    setupFinish: 'Finish',
    setupSkip: 'Skip',
    setupCloudPricing: 'See pricing',
    setupCloudRegistered: 'Registered with cloud',

    // Cloud→hub identity claim (`POST /auth/claim`, PLAN.md F13): the app links
    // this hub to the signed-in Torik account instead of inheriting the hub's
    // first admin. A failure here is a dead end the person has to act on.
    claim: {
      noMatchingUser: "Your Torik account isn't set up on this hub yet. Ask an administrator to invite {email}.",
      userDisabled: 'This account is disabled on this hub. Ask an administrator to enable it.',
      invalidCloudToken: 'Your Torik sign-in could not be verified. Please sign in again.',
      failed: 'This hub could not be linked to your Torik account. Ask an administrator for help.',
    },

    // Onboarding — the passwordless ways into a hub (PLAN.md §3.3). Which of
    // these actually renders is decided per runtime mode in
    // `src/config/onboarding.ts`; the copy here covers all of them.
    onboarding: {
      themeLight: 'Switch to light mode',
      themeDark: 'Switch to dark mode',
      redirecting: 'Redirecting to sign in...',
      redirectingButton: 'Redirecting...',
      cloudSignIn: 'Sign in with your {platform} account',
      cloudWebIntro: 'Sign in with your {platform} web account to access your servers.',
      joinTitle: 'Ways to get in',
      joinBody:
        'Torik hubs are opened with your Torik account, or with a local password this hub checks itself. An installer is invited like anyone else, and gets in only inside the hours an administrator sets.',
      joinInviteHint: 'Been invited? Sign in with your Torik account — the server will be there.',
      // The hub's own **local password** (decision D1,
      // `docs/plans/LOCAL-HTTP-AND-CLOUD-ADOPTION.md`; hub side card
      // `t_7fb9c250`, app half `services/hubLocalPassword.ts`): the door a
      // person standing at the controller still has when the house's internet
      // is not there. Offered only on the surface the hub itself serves
      // (`config/onboarding.ts` → `localPassword`); the app and the desktop
      // reach the same door through the Local servers page, so this screen does
      // not repeat it there.
      localPasswordOpen: 'Sign in with a local password',
      localPasswordTitle: 'Sign in with your local password',
      localPasswordHint:
        'This password is checked here, on this hub — no internet needed. It is the way in when the house is offline.',
      localPasswordIdentifierLabel: 'Your hub username or account email',
      localPasswordIdentifierPlaceholder: 'e.g. tadej',
      localPasswordLabel: 'Local password',
      // R5 of the plan: the first offline-verifiable secret this hub has ever
      // had, so the surface says plainly what nothing else can do for it.
      localPasswordNoRecovery: 'Nothing recovers this password without your Torik account.',
      scanHubCode: 'Scan the QR code on your hub',
      scanHint: 'Point the camera at the QR code printed on the hub’s sticker.',
      cameraDenied: 'The camera could not be started. Allow camera access and try again.',
      cameraUnsupported: 'This browser cannot read QR codes. Use the Torik app instead.',
      notAHubCode: 'That is not a hub’s sticker. Scan the QR code printed on the hub.',
      localServer: 'Connect to a local server',
      localServerHint: 'Find Torik hubs on this network.',
      searchingNetwork: 'Searching your network...',
      networkHubs: 'Hubs on this network',
      noHubsFound: 'No hubs found on this network.',
      // A discovered hub, read through the decision table (`resolveDiscoveredHubPlan`):
      // a hub this device holds a credential for is opened, one it holds nothing
      // for is joined, and a build too old to answer `GET /api/hub/identity` is
      // still listed — just named for what it is.
      hubJoinHint: 'Join this hub',
      hubOpenHint: 'This device is already set up here',
      hubNotIdentified: 'Did not identify itself (older software)',
      rescan: 'Search again',
      manualEntry: 'Enter the address instead',
      ipPlaceholder: 'IP address',
      portPlaceholder: 'Port',
      connect: 'Connect',
      hubNotReachable: 'That address did not answer. Check the IP address and port.',
      back: 'Back',
      legacyNotice: 'Administrator fallback sign-in — this hub is moving away from local passwords.',
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Enrolment codes are gone (card `t_2f0c4a91`): nothing mints, types or
  // scans one. What is left of this namespace is what other modules still read
  // by literal key — the hub's name when the app knows none
  // (`enroll.hub.unknown`; `stores/auth.ts`, `HubNeedsCredential.vue`,
  // `OnboardingPassSheet.vue`, `FindLocalServersModal.vue`) and the role a
  // session holds (`enroll.session.*`; `utils/sessionRole.ts`).
  // ──────────────────────────────────────────────────────────────────────────
  enroll: {
    hub: {
      unknown: 'This hub',
    },

    /** What a session's role is called on screen (`utils/sessionRole.ts`). */
    session: {
      roleLabel: 'It acts as {role} on this hub.',
      validUntil: 'Credential valid until {date}',
      roleOwner: 'the owner',
      roleAdmin: 'an administrator',
      roleResident: 'a resident',
      roleGuest: 'a guest',
      roleService: 'a service account',
      roleInstaller: 'an installer',
      roleViewer: 'a viewer',
      roleUnknown: 'a device',
      continue: 'Continue to the app',
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Navigation & Sidebar
  // ──────────────────────────────────────────────────────────────────────────
  nav: {
    dashboard: 'Dashboard',
    home: 'Home',
    rooms: 'Rooms',
    devices: 'Devices',
    cameras: 'Cameras',
    security: 'Security',
    climate: 'Climate',
    water: 'Water',
    appliances: 'Appliances',
    wellness: 'Wellness',
    lights: 'Lights',
    shades: 'Shades',
    energy: 'Energy',
    weather: 'Weather',
    maintenance: 'Maintenance',
    apps: 'Apps',
    main: 'Main',
    settings: 'Settings',
    media: 'Media',
    intercom: 'Intercom',
    map: 'Map',
  },
  roomsNav: {
    allRooms: 'All Rooms',
    unassigned: 'Unassigned',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Settings & System
  // ──────────────────────────────────────────────────────────────────────────
  settings: {
    settingsUpdates: {
      hubUpdateTitle: 'Hub update available',
      hubUpdateBody:
        'A new hub update is available: v{version}. Open Settings → System → Appliance update to install it.',
      checkUpdates: 'Check for Updates',
      checking: 'Checking...',
      updateFailed: 'Update failed',
      downloadingUpdate: 'Downloading update...',
      updateReady: 'Update ready — restart to install',
      restartInstall: 'Restart & Install',
      upToDate: 'You are on the latest version',
      appUpdate: 'App Update',
      checkFailed: 'Update check failed',
    },
    // Addon service cards (settings/AddonServicesSection.vue): the voice
    // services and the browser & automation addon the hub runs as containers.
    addons: {
      services: 'Services',
      refresh: 'Refresh',
      noServices: 'No services available.',
      status: {
        running: 'Running',
        stopped: 'Stopped',
        notInstalled: 'Not installed',
        installing: 'Installing',
        updating: 'Updating',
        uninstalling: 'Uninstalling',
        error: 'Error',
      },
      start: 'Start',
      stop: 'Stop',
      install: 'Install',
      logs: 'Logs',
      logsTitle: 'Logs (last 100 lines)',
      generateFromDevices: 'Generate from devices',
      generating: 'Generating…',
      availableCommands: 'Available commands',
      saveRestart: 'Save & Restart',
      savingRestart: 'Saving & Restarting...',
      savedRestarting: 'Saved — container restarting',
      errorPrefix: 'Error:',
      errorLoad: 'Failed to load services',
      errorSaving: 'Error saving config',
      errorStart: 'Failed to start service',
      errorStop: 'Failed to stop service',
      errorInstall: 'Failed to install service',
    },
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Account & Profile
  // ──────────────────────────────────────────────────────────────────────────
  // Settings Sidebar Navigation
  settingsNav: {
    configuration: 'Configuration',
    general: 'General',
    torikApi: 'Torik API',
    automations: 'Automations',
    rooms: 'Rooms',
    devices: 'Devices',
    functions: 'Functions',
    modes: 'Modes',
    integrations: 'Integrations',
    security: 'Security',
    energy: 'Energy',
    weather: 'Weather',
    climate: 'Climate',
    water: 'Water',
    appliances: 'Appliances',
    lights: 'Lights',
    shades: 'Shades',
    scenes: 'Scenes',
    routines: 'Routines',
    charts: 'Charts',
    system: 'System',
    users: 'Users',
    presence: 'Presence Tracking',
    agent: 'Agent',
    demo: 'Demo',
    backup: 'Backup',
    library: 'Library',
    gallery: 'Gallery',
    model: '3D Model',
    dashboards: 'Dashboards',
    installer: 'Installer',
    logs: 'Logs',
    dangerZone: 'Danger Zone',
  },

  installOffer: {
    text: 'Get the Torik app — better on your phone.',
    button: 'Install',
    dismiss: 'Not now',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Views - Nested object for all view sections
  // ──────────────────────────────────────────────────────────────────────────
  views: {
    // Map view (views/Map.vue + components/map/MapZonePanel.vue): the hub's own
    // geofence zones and the cloud areas the cloud owns (card `t_6b57f8f2`, plan
    // docs/plans/GEOFENCING-E2E.md §2.4). `cloudRefusal*` are read indirectly
    // (`cloudAreaRefusalNotice(...).key`) — see locales/localeAudit.ts.
    map: {
      title: 'Geofence Zones',
      empty: 'No zones yet. Create one with “Add zone”.',
      radiusMeters: '{meters} m radius',
      flyTo: 'Fly to zone',
      editZone: 'Edit zone',
      deleteZone: 'Delete zone',
      zoneName: 'Zone name',
      radiusShort: 'Radius (m)',
      radiusMetersLabel: 'Radius (meters)',
      lat: 'Lat',
      lon: 'Lon',
      newZone: 'New Zone',
      globalZone: 'Global zone (visible to all users)',
      createZone: 'Create Zone',
      clickHint: 'Click on the map to create a new zone',
      addZone: 'Add zone',
      moveHint: 'Select an area, then drag its handle to move it',
      layerUsers: 'Users',
      layerZones: 'Zones',
      centerOnHub: 'Center on hub',
      manageZones: 'Manage zones',
      hubMarker: 'Hub',
      sourceLocal: 'On this server',
      sourceCloud: 'Cloud area',
      cloudManaged: 'Cloud-managed',
      cloudTitle: 'Cloud areas',
      cloudAdd: 'Add cloud area',
      cloudPickHint: 'Click on the map to place the new cloud area',
      cloudLoading: 'Loading cloud areas…',
      cloudEmpty: 'No cloud areas yet.',
      cloudRetry: 'Try again',
      cloudAdminOnly: 'Only a server owner or administrator can change its cloud areas.',
      cloudAreaName: 'Area name',
      cloudCreate: 'Create area',
      cloudDeleteConfirm: 'Delete cloud area "{name}"?',
      cloudRefusalForbidden: 'This account is not allowed to change this server’s cloud areas.',
      cloudRefusalNotMember: 'This account has no role on this server.',
      cloudRefusalNotAdmin: 'Only a server owner or administrator can change its cloud areas.',
      cloudRefusalNotFound: 'This cloud area no longer exists.',
      cloudRefusalInvalidRadius: 'The radius must be between 50 and 5000 metres.',
      cloudRefusalInvalidArea: 'Check the area’s name and position.',
      cloudRefusalGeneric: 'The cloud refused the change.',
    },

    // Rooms view
    rooms: {
      pageTitle: 'Rooms',
      searchPlaceholder: 'Search by room or area...',
      loading: 'Loading...',
      emptyTitle: 'No rooms',
      emptyDescription: 'Get started by creating a new room.',
      noResultsTitle: 'No rooms found',
      noResultsDescription: 'Try a different search term.',
      unassignedRoomsSectionTitle: 'Unassigned Rooms',
      loadError: 'Failed to load data',
    },

    // Room scan (card `t_90febbcf`, A2): the capture entry and its panel
    // (`components/scan/RoomScanEntry.vue` + `RoomScanPanel.vue`). The capture
    // itself is the native `RoomScan` plugin (iOS, LiDAR); `unsupported*` are the
    // honest answers a device without LiDAR reads (plan §7).
    // 3D Scans (card `t_8ae1d20a`, E2): the settings page that lists the scans
    // stored on THIS hub (`views/Settings/SettingsModel.vue`) with their upload,
    // replace, download and delete, and the capture button that feeds it.
    model: {
      title: '3D Model',
      subtitle: 'The one 3D model this hub serves, and the scans it can be built from.',
      homeTitle: 'Home 3D model',
      homeIntro: 'Every page that shows the house renders these bytes — there is one model per hub.',
      upload: 'Upload model…',
      replace: 'Replace…',
      download: 'Download',
      delete: 'Delete',
      active: 'Active',
      updated: 'Updated',
      loading: 'Reading the model…',
      loadFailed: 'The hub’s model could not be read',
      empty: 'No 3D model on this hub yet — upload an IFC, GLB or glTF file, or use a 3D scan below.',
      uploading: 'Uploading the model…',
      uploadDone: 'The model was uploaded.',
      uploadFailed: 'The model could not be uploaded',
      badFormat: 'Please select an IFC (.ifc), GLB (.glb) or glTF (.gltf) file.',
      tooLarge: 'The model file must be smaller than 200 MB.',
      downloading: 'Downloading the model…',
      downloadDone: 'The model was saved as “{name}”.',
      downloadFailed: 'The model could not be downloaded',
      deleting: 'Removing the model…',
      deleteTitle: 'Remove the 3D model',
      deleteMessage:
        'Remove the hub’s 3D model “{name}”? Every page that shows the house loses it, and the 3D view stays empty until another model is uploaded.',
      deleteDone: '“{name}” was removed.',
      deleteFailed: 'The model could not be deleted',
      scanIsModel: '“{name}” is now the hub’s 3D model.',
    },
    scans: {
      title: '3D Scans',
      subtitle: 'Room scans stored on this hub. Scan a room with the camera, or upload a capture package from a phone.',
      scanRoom: 'Scan a room',
      scanRoomHint: 'A finished scan is uploaded to this hub on its own.',
      uploadPackage: 'Upload package',
      refresh: 'Refresh',
      hubTitle: 'On the hub',
      deviceTitle: 'Captures on this device',
      deviceEmpty: 'No captures on this device yet.',
      deviceOnHub: 'On the hub',
      deviceUpload: 'Upload',
      loading: 'Loading scans…',
      loadFailed: 'Could not load the scans from this hub',
      emptyTitle: 'No scans yet — scan your first room',
      emptyHint:
        'A scan package is the folder a capture writes (scan.json, model.usdz, the plan …). Upload one here, or scan with the app.',
      modeRoom: 'One room',
      modeStructure: 'Floor',
      rooms: '{count} room | {count} rooms',
      files: '{count} file | {count} files',
      capturedAt: 'Captured',
      updatedAt: 'Updated',
      download: 'Download',
      replace: 'Replace',
      delete: 'Delete',
      close: 'Close',
      retry: 'Try again',
      working: 'Working…',
      reading: 'Reading the capture from this device…',
      uploading: 'Uploading to the hub…',
      replacing: 'Replacing on the hub…',
      downloading: 'Downloading from the hub…',
      progress: '{percent}% uploaded',
      progressUnknown: 'The hub is receiving the package…',
      uploadDone: '"{name}" is on the hub.',
      replaceDone: '"{name}" was replaced.',
      deleteDone: '"{name}" was deleted from the hub.',
      downloadDone: '"{name}" saved — {count} file | "{name}" saved — {count} files',
      deleteTitle: 'Delete this scan?',
      deleteMessage: 'Delete "{name}" from the hub? Its files are removed with it and this cannot be undone.',
      validationTitle: 'This package was not sent',
      replaceTitle: 'Replace "{name}"',
      replaceHint: 'The new package takes the scan’s place — the files it holds now are removed.',
      replaceChooseCapture: 'A capture on this device',
      replaceChooseFiles: 'Choose files from this device…',
      replaceNoCaptures: 'No captures on this device.',
      replaceUseCapture: 'Use this capture',
      invalid: {
        empty: 'Choose the files of a capture package first.',
        tooManyFiles: 'A package carries at most {max} files.',
        fileTooLarge: '"{name}" is larger than {max} MB.',
        packageTooLarge: 'The package is larger than {max} MB.',
        badFileName: '"{name}" cannot be stored — a package name has no path, no leading dot and no trailing dot or space.',
        duplicateName: '"{name}" is in the package twice.',
        missingUsdz: 'The package needs the room’s geometry — a .usdz file.',
        badScanDoc: 'The scan.json is not a torik.scan/v0 document.',
        generic: 'The package cannot be sent as it is.',
      },
      warnings: {
        missingScanDoc: 'No scan.json in the package — the hub stores it as a room-level capture.',
      },
      errors: {
        noHub: 'This hub cannot be reached right now.',
        auth: 'The hub refused this session — sign in again.',
        validation: 'The hub refused the package.',
        readFailed: 'The capture could not be read from this device.',
        uploadFailed: 'The upload did not finish.',
        downloadFailed: 'The download did not finish.',
        saveFailed: 'The file could not be saved on this device.',
        unknown: 'The transfer did not finish.',
      },
      model: {
        title: 'Scan 3D',
        intro: 'Scan a room with the camera — a finished capture is uploaded to this hub on its own, and any scan here can become the 3D model of the house on this hub.',
        scan3d: 'Scan 3D',
        empty: 'No scans on this hub yet — start with “Scan 3D”.',
        useScan: 'Use as model',
        selected: 'Chosen as the model',
        noModel: 'No model file in this package',
        loadingFile: 'Reading the scan from the hub…',
        attaching: 'Attaching the scan as the hub’s 3D model…',
        fetchFailed: 'The scan could not be read off the hub',
        attachFailed: 'The scan could not become the hub’s 3D model',
      },
    },

    roomScan: {
      entryTitle: 'Scan rooms with the camera',
      entry: 'Scan',
      title: 'Room scan',
      checking: 'Checking whether this device can scan…',
      unsupportedTitle: 'This device cannot scan rooms',
      unsupportedLidar: 'RoomPlan needs an iPhone Pro or iPad Pro — the LiDAR scanner is what measures the room.',
      unsupportedPlatform: 'Room capture runs in the Torik app on an iPhone Pro or iPad Pro (LiDAR).',
      errorTitle: 'The scan did not finish',
      nameLabel: 'Name this capture (optional)',
      namePlaceholder: 'e.g. Living room',
      startRoom: 'Scan a room',
      startStructure: 'Scan a whole floor',
      startHint: 'The camera view walks you through it — move slowly and keep the room lit.',
      capturingRoom: 'Capturing the room…',
      capturingRoomFloor: 'Capturing this room of the floor…',
      capturingHint: 'Walk the room, then tap Done in the camera view.',
      structureRooms: 'Rooms captured: {count}',
      structureHint: 'Each Done saves one room; Finish merges them into one model.',
      scanAnother: 'Scan another room',
      finish: 'Finish',
      saved: 'Saved: {label} · {count} room | Saved: {label} · {count} rooms',
      scanFile: 'Model data',
      capturesTitle: 'On this device',
      capturesEmpty: 'No captures on this device yet.',
      modeRoom: 'One room',
      modeStructure: 'Floor',
      rooms: '{count} room | {count} rooms',
      deleteConfirm: 'Delete the capture “{label}”? Until the upload flow ships it exists only on this device.',
    },

    // Devices view
    devices: {
      pageTitle: 'Devices',
      searchPlaceholder: 'Search devices...',
      groupByRooms: 'Rooms',
      groupByTypes: 'Types',
      loading: 'Loading devices...',
      noResultsTitle: 'No devices found',
      noResultsDescription: 'Try a different search term.',
      emptyTitle: 'No devices',
      emptyDescription: 'Get started by adding a new device.',
      noRoomLabel: 'No Room',
      noRoomsWithDevicesTitle: 'No rooms with devices',
      noRoomsWithDevicesDescription: 'Add devices and assign them to rooms to see them organized here.',
      unknownType: 'Unknown Type',
      emptyTypeViewDescription: 'Add devices to see them organized by type.',
      deleteConfirmTitle: 'Delete Device',
      deleteConfirmMessage: 'Are you sure you want to delete {deviceName}?',
      cancelButton: 'Cancel',
      deleteButton: 'Delete',
      deletingButton: 'Deleting...',
      loadError: 'Failed to load data',
    },

    // Scenes view
    scenes: {
      pageTitle: 'Scenes',
      searchPlaceholder: 'Search scenes...',
      addSceneButton: 'Add Scene',
      reorderButton: 'Reorder',
      doneButton: 'Done',
      cancelButton: 'Cancel',
      deleteButton: 'Delete',
      loading: 'Loading',
      emptyTitle: 'No scenes yet',
      emptyDescription: 'Create a scene to get started.',
      noResultsTitle: 'No scenes match',
      deleteConfirmTitle: 'Delete Scene',
      deleteConfirmMessage: 'Are you sure you want to delete',
      loadError: 'Failed to load scenes',
      executionSuccess: 'Scene executed successfully',
      executionError: 'execution completed with errors',
    },

    // Routines view
    routines: {
      pageTitle: 'Routines',
      loading: 'Loading routines...',
      createButton: 'Create Routine',
      editButton: 'Edit',
      runNowButton: 'Run Now',
      emptyTitle: 'No routines yet',
      emptyDescription: 'Create your first routine to automate your smart home',
      triggerCount: 'trigger | triggers',
      conditionCount: 'condition | conditions',
      actionCount: 'action | actions',
      deleteConfirmMessage: 'Are you sure you want to delete this routine?',
      loadError: 'Failed to load routines',
      deleteError: 'Failed to delete routine',
      enabledMessage: 'Routine enabled',
      disabledMessage: 'Routine disabled',
      deletedMessage: 'Routine deleted',
      executionStartedMessage: 'Routine execution started',
      executionError: 'Failed to execute routine',
      // The row a dead device-value reference paints (Routine*Editor): a stored
      // routine action/condition/trigger whose valueId is no longer on the hub.
      missingDeviceValueTitle: 'This device value no longer exists',
      missingDeviceValueHint: 'Pick a device and property again - the routine cannot run until this is fixed.',
      storedValue: 'Stored value',
      storedValueOn: 'On',
      storedValueOff: 'Off',
      readOnly: 'read-only',
      // What a *run* of the routine did (card t_5db7015a): the last-run line on
      // the card and the Runs tab of the editor read the hub's own execution
      // history (`automation_executions`, served at /routines/:id/executions),
      // which nothing in the app read before.
      lastRunLabel: 'Last run',
      runNever: 'Not run yet',
      runStatusCompleted: 'Completed',
      runStatusFailed: 'Failed',
      runStatusSkipped: 'Skipped',
      runStatusRunning: 'Running',
      runCompletedMessage: 'The routine ran',
      runSkippedMessage: 'The routine ran, but its conditions were not met',
      runFailedMessage: 'The run failed: {reason}',
      runFailedNoReason: 'The run failed',
      triggerManual: 'Manual',
      historyEmpty: 'This routine has not run yet.',
      historyEmptyHint: 'It runs when one of its triggers fires, or when you press Run Now.',
      historyLoadError: 'The run history could not be loaded.',
      historyTruncated: 'Only the most recent 50 runs are shown.',
      // The hub's own sentences (RoutineService.ts), recognised by exact match;
      // anything else is painted as the hub wrote it (utils/routineRuns.ts).
      errorNoValueReference: 'The routine points at a device value the hub no longer has.',
      errorMissingValueId: 'The action is missing its device value.',
      errorMissingSceneId: 'The action is missing its scene.',
      errorSceneNotFound: 'The scene this action runs no longer exists.',
      errorMissingUrl: 'The webhook action is missing its URL.',
      errorMissingNotificationText: 'The notification action is missing its title or message.',
      errorRoutineNotFound: 'The routine no longer exists on the hub.',
    },

    // Media view
    media: {
      audioZonesTitle: 'Audio Zones',
      newZoneButton: 'New Zone',
      createZoneTitle: 'Create Zone',
      noAudioZonesTitle: 'No audio zones yet',
      noAudioZonesDescription: 'Create a zone and add panels or apps to it.',
      pausedLabel: 'Paused',
      noMediaPlayersTitle: 'No media players',
      noMediaPlayersDescription: 'Add a Media integration to discover players.',
      goToIntegrations: 'Go to Integrations',
      noLocalMediaMessage: 'No media in the library yet.',
    },

    // Cameras view
    cameras: {
      pageTitle: 'Cameras',
      liveTab: 'Live',
      playbackTab: 'Playback',
      onlineLabel: 'online',
      intercomLabel: 'intercom',
      unlockSecretCamerasTitle: 'Enter PIN to view secret cameras',
      secretButton: 'Secret',
      editButton: 'Edit',
      doneButton: 'Done',
      secretCameraTitle: 'Secret camera - hidden from guests',
      loading: 'Loading cameras...',
      noCamerasFoundMessage: 'No cameras found. Add cameras in the Integrations settings.',
      refreshSnapshotTitle: 'Refresh snapshot',
      privacyBlurTitle: 'Privacy blur',
      privacyBlurMenuTitle: 'Blur duration',
      blur5Min: '5 minutes',
      blur15Min: '15 minutes',
      blur30Min: '30 minutes',
      blur1Hour: '1 hour',
      blur2Hours: '2 hours',
      removeBlurButton: 'Remove blur',
      muteMotionNotificationsTitle: 'Mute motion notifications',
      muteMotionNotificationsMenuTitle: 'Mute motion notifications for',
      unmuteMotionNotificationsButton: 'Unmute motion notifications',
      intercomBadge: 'Intercom',
      callDoorphoneTitle: 'Call doorphone',
      editModeInstructions: 'Use arrows to reorder · Click lock to mark as secret',
      cancelButton: 'Cancel',
      savingButton: 'Saving...',
      saveOrderButton: 'Save order',
      selectCamerasMessage: 'Select cameras above to start playback',
      // A live view whose ICE never completes: the camera's WHEP answer was
      // accepted and no media path came up. `services/mediaPath.ts` bounds the
      // wait and names the state; the players paint this sentence (card t_20b35b28).
      mediaPathFailed:
        'Could not establish a media path to this camera — it accepted the request, but no video or audio connection came up. Try again, open the camera’s own app, or check go2rtc’s log on the hub.',
      // Why the attempt ended, painted under the sentence (card t_4d5b3263):
      // the three causes need different next actions, and only the cause is
      // said — the gather census and the timings stay in the console and in
      // `services/mediaPath.ts`'s own warnings, never on the screen.
      mediaPathReasonBudget:
        'No network path to the camera completed in time — this is usually the hub’s network, not the camera.',
      mediaPathReasonFailed: 'The network path to the camera failed — check the hub’s network before retrying.',
      mediaPathReasonNoCandidates: 'This device could not prepare a connection to the camera.',
    },

    // Account view
    account: {
      profileInformationTitle: 'Profile Information',
      profilePictureLabel: 'Profile Picture',
      changePictureButton: 'Change Picture',
      pictureFormatHint: 'PNG or JPG (max 2MB)',
      // The account page used to paint a Username field and row; the handle is a
      // derived, invisible column (hub card t_a8eb402a), so both are gone (card
      // t_33ae96b2) and their two strings went with them.
      fullNameLabel: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      emailLabel: 'Email Address',
      emailPlaceholder: "user{'@'}example.com",
      phoneLabel: 'Phone Number',
      phonePlaceholder: '+1 (555) 123-4567',
      bioLabel: 'Bio',
      bioPlaceholder: 'Tell us about yourself',
      saveProfileButton: 'Save Profile',
      savingButton: 'Saving...',
      securitySettingsTitle: 'Security Settings',
      // The hub's own local password (decision D1): per user, optional, and
      // checked by this hub with no cloud round trip — the door a person
      // standing at the controller still has when the house's internet is gone.
      localPasswordTitle: 'Local password',
      localPasswordSetHint: 'Sign in with this password when the internet is down. This hub checks it itself.',
      localPasswordUnsetHint:
        'Set a password to sign in to this hub directly when the internet is down. It is optional — without one, a device connects to this hub as usual.',
      localPasswordCurrent: 'Current password',
      localPasswordNew: 'New password (at least 8 characters)',
      localPasswordConfirm: 'Repeat the new password',
      localPasswordMismatch: 'The two passwords do not match.',
      localPasswordNoRecovery: 'Nothing recovers this password without your Torik account.',
      localPasswordSet: 'Set password',
      localPasswordChange: 'Change password',
      localPasswordSaving: 'Saving…',
      localPasswordSaved: 'Local password saved.',
      // The way out (owner, 2026-09-30: *"we would need the 'Unset' local
      // password… in both pages"*): the offline door can be taken away again —
      // after a confirmation, because the house it opens is the one with the
      // internet down.
      localPasswordRemove: 'Remove local password',
      localPasswordRemoveConfirm:
        'Remove the local password? Signing in to this hub with the internet down will not work until a new one is set.',
      localPasswordRemoving: 'Removing…',
      localPasswordRemoved: 'Local password removed.',
      preferencesTitle: 'Preferences',
      themeLabel: 'Theme',
      languageLabel: 'Language',
      notificationsTitle: 'Notifications',
      savePreferencesButton: 'Save Preferences',
      memberSinceLabel: 'Member Since',
      lastLoginLabel: 'Last Login',
      sessionTitle: 'Session',
      logoutDescription: 'Log out from your current session.',
      profileTabLabel: 'Profile',
      securityTabLabel: 'Security',
      preferencesTabLabel: 'Preferences',
      infoTabLabel: 'Account Info',
      themeSystemDefault: 'System Default',
      themeLightOption: 'Light',
      themeDarkOption: 'Dark',
      languageEnglish: 'English',
      languageSpanish: 'Español',
      languageFrench: 'Français',
      languageGerman: 'Deutsch',
      languageItalian: 'Italiano',
      languageSlovenian: 'Slovenščina',
      temperatureUnitsLabel: 'Temperature Units',
      celsiusOption: 'Celsius (°C)',
      fahrenheitOption: 'Fahrenheit (°F)',
      notificationGroupAlerts: 'Alerts',
      notificationGroupAlertsDetail: 'Device alerts and hub trouble',
      notificationGroupAutomation: 'Automation & notices',
      notificationGroupAutomationDetail: 'Automations, routines, timers and reminders',
      notificationGroupMessages: 'Calls & messages',
      notificationGroupMessagesDetail: 'Calls and chat messages',
      notificationGroupPartly: 'Partly on',
      notificationGroupsUnavailable: 'Your notification settings could not be loaded.',
      userNotifications: {
        typeDeviceAlert: 'Device alerts',
        typeSystem: 'Hub trouble',
        typeAutomation: 'Automations',
        typeRoutine: 'Routines',
        typeInfo: 'Notices',
        typeCall: 'Calls',
        typeChat: 'Chat messages',
        channelPush: 'Push',
        channelInApp: 'In-app',
        unavailable: 'This person’s notification settings could not be loaded.',
      },
      accountInformationTitle: 'Account Information',
      logOutButton: 'Log Out',
      loggingOutButton: 'Signing out...',
      notSetLabel: 'Not set',
      roleLabel: 'Role',
      userRoleDefault: 'user',
    },

    // Notifications view
    notifications: {
      title: 'Notifications',
      markAllAsRead: 'Mark All as Read',
      clearAll: 'Clear All',
      tabAll: 'All Notifications',
      tabUnread: 'Unread',
      tabByType: 'By Type',
      loading: 'Loading notifications...',
      emptyState: 'No notifications yet',
      emptyUnread: 'All caught up! No unread notifications',
      noNotificationsOfType: 'No notifications of this type',
      markAsUnread: 'Mark as Unread',
      markAsRead: 'Mark as Read',
      deleteButton: 'Delete',
      deleteAllConfirm: 'Delete all notifications?',
      timeJustNow: 'Just now',
      timeMinutesAgo: 'm ago',
      timeHoursAgo: 'h ago',
      timeDaysAgo: 'd ago',
    },

    // Alarms view
    alarms: {
      title: 'Alarms',
      newAlarm: 'New Alarm',
      emptyState: 'No alarms yet. Tap New Alarm to get started.',
      editAlarmTitle: 'Edit Alarm',
      labelField: 'Label',
      labelPlaceholder: 'Wake up',
      timeField: 'Time',
      repeatField: 'Repeat',
      noDaysHelp: 'No days selected = one-time alarm',
      snoozeField: 'Snooze duration (minutes)',
      soundField: 'Sound',
      cancel: 'Cancel',
      saveAlarm: 'Save Alarm',
      saving: 'Saving…',
      confirmDelete: 'Delete this alarm?',
      daysSu: 'Su',
      daysMo: 'Mo',
      daysTu: 'Tu',
      daysWe: 'We',
      daysTh: 'Th',
      daysFr: 'Fr',
      daysSa: 'Sa',
    },

    // People view
    people: {
      title: 'People',
      searchPlaceholder: 'Search conversations...',
      loading: 'Loading...',
      emptyPeople: 'No people found',
      selfDMDescription: 'Save messages, files & links',
      selfBadge: 'Self',
      selfConversationHint: 'Only you can see this',
      participantsLabel: 'participants',
      audioCallTitle: 'Audio call',
      videoCallTitle: 'Video call',
      moreOptionsTitle: 'More options',
      deleteAllMessages: 'Delete all messages',
      captionPlaceholder: 'Add a caption (optional)...',
      messagePlaceholder: 'Type a message...',
      someoneTyping: 'Someone is typing...',
    },

    // Areas view

    // Integrations view
    integrations: {
      title: 'Integrations',
      addIntegration: 'Add Integration',
      loading: 'Loading integrations...',
      emptyTitle: 'No integrations',
      emptyMessage: 'Get started by adding a new integration.',
      delete: 'Delete',
      deleteTitle: 'Delete Integration',
      confirmDeletePrefix: 'Are you sure you want to delete',
      confirmDeleteSuffix: '? This will also remove all associated devices.',
      cancel: 'Cancel',
      deleting: 'Deleting...',
      statusDisabled: 'Disabled',
      statusConnected: 'Connected',
      statusDisconnected: 'Disconnected',
      statusNotConfigured: 'Not configured',
      statusDemo: 'Demo',
      demoMode: 'Demo mode',
      demoModeOn: 'Demo on',
      demoModeSaving: 'Saving…',
      demoModeTooltip:
        'Demo mode: this integration is detached from the real hardware and feeds simulated values. Toggle off to reconnect for real.',
      demoModeHint: 'Simulate this integration — no traffic to the real device or bus.',
      demoModeActiveHint: 'Running on simulated values (demo driver). No hardware is contacted.',
    },

    // Agent view
    agent: {
      title: `${brand.agentName}`,
      subtitle: 'Voice & text assistant',
      reset: 'Reset',
      resetTitle: 'Reset if agent is stuck',
      clearButton: 'Clear',
      history: 'History',
      emptyTitle: 'How can I help you?',
      emptyMessage: 'Try asking me to control devices, check status, or manage your home',
      thinking: 'Thinking...',
      stop: 'Stop',
      listeningIndicator: '🎤 Listening... Speak now',
      placeholder: 'Type a message or use voice...',
      noHistory: 'No conversation history yet',
      suggestionLights: 'Turn on living room lights',
      suggestionTemperature: "What's the temperature?",
      suggestionCameras: 'Show me the cameras',
      suggestionScene: 'Run good night scene',
    },

    // Room view
    room: {
      floorLabel: 'Floor',
      addDevice: 'Add Device',
      editRoom: 'Edit Room',
      moreActions: 'More actions',
      emptyTitle: 'No devices in this room',
      emptyMessage: 'Click "Add Device" to add one to this room.',
      scenesSection: 'Scenes',
      addScene: 'Add scene',
      reorderScenes: 'Reorder scenes',
      reorder: 'Reorder',
      dragScenesToReorder: 'Drag scenes to reorder',
      chartsSection: 'Charts',
      deleteTitle: 'Delete Device',
      confirmDeletePrefix: 'Are you sure you want to delete',
      confirmDeleteSuffix: '?',
      cancel: 'Cancel',
      deleting: 'Deleting...',
      devicesSection: 'Devices',
      editLayout: 'Edit layout',
      done: 'Done',
      editHint: 'Drag devices to reorder. Use the toolbar on each device to choose how it is displayed.',
      dragToReorder: 'Drag to reorder',
      deviceSettings: 'Device settings',
      displayAuto: 'Auto',
      displayButton: 'Button',
      displayFull: 'Full width',
      displayIcon: 'Icon',
      layout: 'Layout',
      layoutImageAlt: '{room} layout',
      dragHandle: 'drag',
      otherType: 'Other',
    },

    dashboardNew: {
      actionsMenuTitle: 'Dashboard actions',
      addDashboardAction: 'Add dashboard',
      addWidgetAction: 'Add widget',
      editDashboardAction: 'Edit dashboard',
      toggleEditModeAction: 'Toggle edit mode',
      gridSettingsAction: 'Grid settings',
      loadingMessage: 'Loading dashboard...',
      retryButton: 'Retry',
      defaultDashboardName: 'Dashboard',
      loadFailed: 'Failed to load dashboard',
      createFailed: 'Failed to create dashboard',
      createFromTemplateFailed: 'Failed to create dashboard from template',
      welcomeHeading: `Welcome to ${brand.platformCompact}`,
      welcomeDescription: 'Get started by creating your first dashboard from a template or from scratch.',
      createDashboardButton: 'Create Dashboard',
    },

    // Settings views
    settingsSystem: {
      securityUpdateTooltip: 'Security update',
      currentVersionLabel: 'Current Version',
      channelLabel: 'Channel',
      lastCheckedLabel: 'Last checked',
      updateAvailableLabel: 'Update available',
      lastUpdatedLabel: 'Last updated',
      kernelLabel: 'kernel',
      checkingForUpdates: 'Checking for updates…',
      applianceUpdateHint: 'Click "Check for Updates" to check for new appliance releases.',
      platformdUpToDate: 'Platform daemon is up to date.',
      platformdNoUpdate: 'No platform daemon update available.',
      platformdUpdateHint: 'Click "Check for Updates" to check for platform daemon releases.',
      systemUpdatesDisabled: 'System updates are not enabled on this device.',
      updateCheckFailed: 'Failed to check for appliance updates.',
      updateStatusInProgress: 'An update is currently in progress.',
      updateInstalling: 'Update is being installed — the hub will restart when it finishes.',
      updateStatusInstalling: 'An update is being installed — the hub will restart when it finishes.',
      updateStatusNotRegistered: 'Cloud registration is required before this hub can check for appliance releases.',
      updateStatusBlocked: 'This hub needs an intermediate upgrade before it can install the latest appliance release.',
      updateStatusNoRelease: 'No published appliance release is currently available for this channel.',
      updateStatusUpToDate: 'This hub is already on the latest published appliance release.',
      updateStatusUnreachable: 'The update service could not be reached.',
      updateStatusNoUpdate: 'No appliance update is currently available.',
      updateStarted: 'Update started. The hub will restart if a new appliance release is installed.',
      updateStartFailed: 'Failed to start appliance update.',
      rollbackInitiated: 'Rollback initiated. The hub will restart if a rollback image is available.',
      rollbackStartFailed: 'Failed to start rollback.',
      systemUpdateCheckFailed: 'Failed to check system updates',
      systemInstallFailed: 'Install failed',

      platformdUpdate: 'Platform daemon',
      platformdUpdateDescription: 'System service that manages networking, hardware and appliance updates',
      installPlatformd: 'Update Platform Daemon',
      confirmInstallPlatformd: 'Update the platform daemon? The service will briefly restart.',
      platformdUpdateInProgress: 'Platform daemon update in progress — the service will restart when it finishes.',
      title: 'System Settings',
      systemName: 'System Name',
      systemNamePlaceholder: `${brand.platformCompact}`,
      systemImage: 'System Image',
      changeImage: 'Change Image',
      uploadImage: 'Upload Image',
      removeImage: 'Remove',
      imageSizeLimit: 'PNG, JPG, or WebP (max 5MB)',
      imageTooLarge: 'Image file is too large (max 10MB)',
      imageUploaded: 'Image uploaded successfully',
      imageRemoved: 'Image removed successfully',
      saveSystemSettings: 'Save System Settings',
      savingSystem: 'Saving...',
      cloudServices: 'Cloud Services',
      licence: 'Licence',
      manageLicence: 'Manage licence',
      noPlan: 'No plan',
      unlimitedSeats: 'Unlimited seats',
      oneSeat: '1 seat',
      seats: 'seats',
      ownerOnly: 'Owner only',
      accessLocalOnly: 'Local access only',
      accessRemote: 'Remote access',
      licensedApps: 'Apps',
      licenceNeedsRegistration: 'Register this server above to receive its licence from the cloud.',
      licencePending:
        'Waiting for the cloud to confirm this server’s licence — this updates on its own within a minute.',
      licenceStale: 'Not confirmed recently',
      licenceStaleHint: 'The cloud has not confirmed this licence in a while — check the connection above.',
      licenceGrantedTo: 'Granted to',
      licenceConfirmed: 'Last confirmed by the cloud',
      justNow: 'just now',
      minutesAgo: 'minutes ago',
      hourAgo: 'hour ago',
      hoursAgo: 'hours ago',
      connected: 'Connected',
      disconnected: 'Disconnected',
      connecting: 'Connecting...',
      connectionError: 'Connection Error',
      notRegistered: 'Not Registered',
      registerCloudDescription: 'Register your hub with cloud services to enable remote access and sharing.',
      cloudAccount: 'Cloud Account',
      cloudAccountEmail: 'Cloud Account Email',
      emailPlaceholder: "email{'@'}example.com",
      cloudUrl: 'Cloud URL',
      registerWithCloud: 'Register with Cloud',
      registering: 'Registering...',
      reconnect: 'Reconnect',
      reconnecting: 'Reconnecting...',
      unregister: 'Unregister',
      unregistering: 'Unregistering...',
      sharedWith: 'Shared With',
      hubNotShared: 'This hub is not shared with anyone else.',
      removeAccess: 'Remove',
      viewOnly: 'View Only',
      control: 'Control',
      admin: 'Admin',
      emailToSharePlaceholder: "user{'@'}example.com",
      add: 'Add',
      adding: 'Adding...',
      loading: 'Loading...',
      saving: 'Saving...',
      settingsSaved: 'Settings saved successfully',
      hubLocation: 'Hub Location',
      locationDescription: 'Hub location is used for weather forecasting and sunrise/sunset calculations.',
      automaticLocation: 'Automatic',
      manual: 'Manual',
      pickLocation: 'Pick Location on Map',
      latitude: 'Latitude',
      latitudePlaceholder: 'e.g., 46.1199',
      longitude: 'Longitude',
      longitudePlaceholder: 'e.g., 14.4992',
      elevation: 'Elevation (meters)',
      elevationPlaceholder: 'e.g., 500',
      location: 'Location',
      saveHubLocation: 'Save Hub Location',
      weather: 'Weather',
      weatherLocation: 'Weather Location',
      searchCityPlaceholder: 'Enter city name',
      units: 'Units',
      metric: 'Metric (°C, m/s)',
      imperial: 'Imperial (°F, mph)',
      saveWeatherSettings: 'Save Weather Settings',
      cityRequired: 'City is required',
      weatherSettingsSaved: 'Weather settings saved successfully',
      citySearchFailed: 'Failed to search cities',
      searchCity: 'Search City',
      enterCityName: 'Enter city name',
      searching: 'Searching...',
      search: 'Search',
      noCitiesFound: 'No cities found',
      enterCitySearch: 'Enter at least 2 characters to search',
      close: 'Close',
      systemIcon: 'System Icon',
      systemIconType: 'Icon Type',
      systemIconGenerated: 'Auto-generated',
      systemIconCustom: 'Custom Image',
      systemIconColor: 'Background Color',
      systemIconTextColor: 'Text Color',
      systemIconUpload: 'Upload Icon',
      systemIconPreview: 'Preview',
      systemIconSizeLimit: 'PNG only (max 500KB)',
      systemIconSaved: 'System icon settings saved',
      saveSystemIconSettings: 'Save System Icon Settings',
      systemIconReset: 'Reset to Default',
      systemIconResetConfirm: 'Reset system icon to default?',
      systemIconResetDone: 'System icon reset to default',
      // Legacy aliases for backward compatibility (keep old keys pointing to new strings)
      applianceUpdate: 'Appliance update',
      applianceUpdateDescription: `${brand.agentName} Hub software and system package updates`,
      checkForUpdates: 'Check for Updates',
      checking: 'Checking...',
      confirmInstall: 'Install update? System will restart.',
      confirmInstallSystem:
        'Install all system package updates? This may take several minutes. The system will continue running.',
      confirmRollback: 'Rollback? System will restart.',
      installAll: 'Install All Updates',
      installUpdate: 'Install Update',
      installing: 'Installing...',
      rollback: 'Rollback to Previous Version',
      rollingBack: 'Rolling back...',
      systemUpdates: 'System Updates',
      upToDate: 'System is up to date',
      updateInProgress: 'Update in progress — the hub will restart when it finishes.',
      // The hub's own HTTPS listener (card `t_d653a49a`): the pair on disk, and
      // the switch that decides whether it is served. The switch here is the
      // product surface for `ssl_enabled` — it wins over the appliance's
      // `.env`; with no setting written, `.env` decides; with neither, the pair
      // does — and the panel names whichever one decided.
      tlsTitle: 'HTTPS listener',
      tlsDescription:
        'Serve this hub over HTTPS with a certificate pair you own. Which switch decided — this hub’s setting or the appliance’s .env — is named beside the state.',
      tlsPairPresent: 'Certificate pair present',
      tlsPairAbsent: 'No certificate pair — nothing is served over HTTPS',
      tlsValidUntil: 'Valid until',
      tlsListenerLabel: 'Listener',
      tlsStateOn: 'On',
      tlsStateOff: 'Off',
      tlsSwitchOn: 'Switch on',
      tlsSwitchOff: 'Switch off',
      tlsSwitching: 'Switching…',
      tlsSwitchedOn: 'HTTPS switched on',
      tlsSwitchedOff: 'HTTPS switched off — http:// is this hub’s only door',
      tlsSwitchedOnNoPair: 'Switched on, but no certificate pair is on disk — nothing is served yet',
      tlsDecidedBySettings: 'decided by this hub’s setting (ssl_enabled)',
      tlsDecidedByEnv: 'decided by the appliance’s .env (ssl_enabled)',
      tlsDecidedByPair: 'decided by the certificate pair on disk',
      tlsUploadTitle: 'Upload a certificate pair',
      tlsCertFile: 'Certificate (.crt/.pem)',
      tlsKeyFile: 'Private key (.key/.pem)',
      tlsUpload: 'Upload',
      tlsUploading: 'Uploading…',
      tlsUploaded: 'Pair stored — the state below is what the hub is doing now',
      tlsUploadFailed: 'The upload failed',
      tlsLoadFailed: 'The listener’s state could not be read',
      tlsSwitchFailed: 'The listener could not be switched',
    },

    settingsUsers: {
      usersManagement: 'People and devices',
      peopleSectionTitle: 'People',
      peopleSectionHint:
        'The people on this hub. A person signs in with their Torik account — there is no local password to set.',
      peopleEmpty: 'Nobody is on this hub yet. Invite the first person by email.',
      serverOwnerRow: 'Owner of this server',
      accountLinked: 'Linked to {email}',
      accountNotLinkedWithEmail: 'No account linked yet — {email}',
      accountNotLinked: 'No account linked yet',
      addUser: 'Add User',
      inviteUser: 'Invite User',
      inviteDescription:
        'Send an email invitation. The person accepts it while signed in to their Torik account — no password and no code to type.',
      inviteRoleHint: 'They get the {role} role on this hub.',
      inviteEmail: 'Email address',
      inviteName: 'Display name (optional)',
      inviteNamePlaceholder: 'Jane Doe',
      inviteNameHint: 'Shown in the people list.',
      inviteCheck: 'Check account',
      inviteChecking: 'Checking…',
      inviteAccountExists:
        'This address already has a Torik account — they will be notified, and the server is in their app right away.',
      inviteAccountAbsent:
        'No Torik account yet — they will get an invitation to join. The server appears on its own once their account is ready.',
      inviteCheckUnavailable: 'Could not reach the cloud to check — you can still send the invitation.',
      inviteRole: 'Role',
      roleResident: 'Resident',
      roleGuest: 'Guest',
      roleAdmin: 'Administrator',
      // An installer is a cloud user with hours, not a code (card `t_2f0c4a91`).
      roleInstaller: 'Installer',
      // The hours this person may sign in — what replaced the installer code.
      accessWindowTitle: 'Access hours',
      accessWindowHint:
        'The hours this person may sign in, checked by the hub itself — online and with the house offline. Leave both empty for no limit.',
      accessWindowFrom: 'From',
      accessWindowUntil: 'Until',
      accessWindowProblem: 'The start has to be before the end.',
      inviteSend: 'Send invitation',
      inviteSending: 'Sending...',
      inviteSent: 'Invitation sent',
      inviteNotSent: 'Invitation created — share the link manually',
      inviteFailed: 'Could not create the invitation',
      inviteShareLink: 'This hub cannot send email directly — share this link:',
      inviteCopyLink: 'Copy',
      inviteCopied: 'Copied',
      usersLoading: 'Loading users...',
      done: 'Done',
      // `passwordNotAvailable` is the edit branch of `components/UserModal.vue`
      // (card `t_e56dc2b2`): the dialog an admin opens to change a person's role,
      // shown where the password control used to be. `passwordLegacyHint` went with
      // the create path's escape hatch — adding a person is an invite now, and an
      // invite has no password.
      passwordNotAvailable:
        'This account signs in with its Torik account or an enrolment grant — it has no local password to set.',
      editPersonTitle: 'Edit person',
      deviceListTitle: 'Connected devices',
      deviceLastSeen: 'Last seen {date}',
    },

    settingsBackup: {
      title: 'Backup & Restore',
      automaticBackupSettings: 'Automatic Backup Settings',
      createBackup: 'Create Backup',
      creatingBackup: 'Creating...',
      backupCreated: 'Backup created successfully',
      backupFailed: 'Failed to create backup',
      configurationOnlyInfo:
        'These backups are for configuration only. Media, camera recordings, camera snapshots, and logged value history are excluded.',
      settingsSaved: 'Backup settings saved',
      settingsLoadFailed: 'Failed to load backup settings',
      settingsSaveFailed: 'Failed to save backup settings',
      backupBeforeUpgrade: 'Create backup before upgrade',
      backupBeforeUpgradeDesc: 'Automatically create a configuration backup before installing a software update.',
      weeklyEnabled: 'Enable weekly backups',
      weeklyEnabledDesc: 'Create a configuration backup every week using the schedule below.',
      weeklyDay: 'Backup day',
      weeklyTime: 'Backup time',
      keepBackupsCount: 'Backups kept on server',
      keepBackupsCountHelp: 'Older local backup files are deleted automatically after new backups are created.',
      destinationSettings: 'Backup destinations',
      networkCopyEnabled: 'Copy backup to network location',
      networkCopyEnabledDesc:
        'After creating the local server backup, copy the same file to a network folder or mounted share.',
      networkBackupPath: 'Network backup path',
      networkBackupPathPlaceholder: '\\\\nas\\otto\\backups or /mnt/backups/otto',
      networkBackupPathHelp:
        'Use a reachable UNC path or mounted folder. Local server retention still applies only to the server backup folder.',
      cloudStorageTitle: 'Cloud storage for subscribers',
      cloudStorageDesc: 'Subscriber cloud backup storage will use the same configuration backup package.',
      cloudStorageComingSoon:
        'Cloud upload is not active yet. This will be enabled after the subscriber backup endpoint is available.',
      localRetentionNote:
        'Manual backups use the same configuration package and follow the same local retention setting.',
      availableBackups: 'Available Backups',
      noBackups: 'No backups available',
      download: 'Download',
      delete: 'Delete',
      deleteBackup: 'Delete Backup',
      confirmDeleteBackup: 'Are you sure you want to delete this backup?',
      backupDeleted: 'Backup deleted',
      restoreFromBackup: 'Restore from Backup',
      warning:
        'Warning: Restoring from backup will overwrite all current data. The system will restart after restoration.',
      selectBackupFile: 'Select Backup File to Restore',
      restoringBackup: 'Restoring...',
      restoring: 'Restoring...',
      validating: 'Validating...',
      backupRestored: 'Backup restored successfully',
      restoreFailed: 'Failed to restore backup',
      confirmRestore: 'This will overwrite all current data. Continue?',
      confirmRestoreTitle: 'Confirm Restore',
      validationWarnings: 'Warnings',
      backupInfo: 'Backup info',
      backupVersion: 'Version',
      backupDate: 'Date',
      incompatibleWarning: 'Database schema is incompatible. Restoring may cause errors or data loss.',
      restoreInitiated: 'Restore initiated. The system will restart shortly.',
      filename: 'Filename',
      created: 'Created',
      fileSize: 'File Size',
      downloadBackup: 'Download Backup',
      selectFile: 'Select File',
      deleteConfirm: 'Delete {filename}?',
      downloadFailed: 'Download failed',
      acceptedFormats: '.otto_backup, .ottohome_backup, .1hub_backup',
      restoreWizardStep1: 'Select Backup File',
      restoreWizardStep2: 'Review Backup',
      restoreWizardStep3: 'Restore Options',
      restoreWizardStep4: 'Confirm Restore',
      restoreWizardCrossServerBanner: 'This backup is from a different server.',
      restoreWizardCrossServerInfo:
        'User passwords use bcrypt and are portable. Existing users can log in with their passwords. The server will restart after restore — you will need to log in again.',
      restoreWizardCreateRecoveryAdmin: 'Create a recovery admin account',
      restoreWizardRecoveryUsername: 'Admin username',
      restoreWizardRecoveryPassword: 'Admin password',
      restoreWizardRecoveryAdminDesc: 'Ensures at least one admin account works after cross-server restore.',
      restoreWizardPreserveHubId: 'Keep current server identity',
      restoreWizardPreserveHubIdDesc: "Prevents overwriting this server's unique ID (recommended).",
      restoreWizardSummaryDb: 'Database will be replaced with the backup contents.',
      restoreWizardSummaryFiles: 'Uploads, Matter data, and system images will be restored.',
      restoreWizardFinalWarning: 'The server will restart after restore. All connected users will be disconnected.',
      restoreWizardUserCount: '{count} user accounts in backup',
      restoreWizardTableCount: '{count} database tables in backup',
      restoreWizardRestoreButton: 'Restore Backup',
      restoreWizardRestoring: 'Restoring backup...',
      restoreWizardUploading: 'Uploading backup file...',
      restoreWizardRestoreComplete: 'Restore complete!',
      restoreWizardRestarting: 'System is restarting. You will be redirected to the login page in {seconds} seconds...',
      restoreWizardContinue: 'Continue',
      restoreWizardBack: 'Back',
      restoreWizardValidating: 'Validating backup file...',
      restoreWizardValidationFailed: 'Failed to validate backup file.',

      valuesBackupTitle: 'Values Backup',
      valuesBackupDesc:
        'Back up device value history (analog logs, counter logs, value logs). The file is downloaded directly and not stored on the server.',
      exportValuesBackup: 'Export Values Backup',
      exportingValues: 'Exporting values backup...',
      valuesRestoreTitle: 'Restore Values',
      valuesRestoreDesc: 'Restore value history from a previous values backup file.',
      valuesRestoreInfo:
        'Only rows with matching device values are imported. Values from non-existent devices are skipped. This is a merge — existing data is preserved.',
      selectValuesBackup: 'Select Values Backup File',
      importingValues: 'Importing values backup...',
      valuesRestoreResult: 'Values restored: {inserted} inserted, {skipped} skipped',
      valuesRestoreFailed: 'Failed to restore values backup',
    },

    settingsAgent: {
      configuration: 'Configuration',
      voice: 'Voice',
      enableAgent: 'Enable Agent',
      enableAgentDesc: 'Activate the AI agent to assist you with voice commands and smart home automation.',
      wakeWord: 'Wake Word',
      wakeWordDesc: `Choose the voice command that activates the agent (e.g., "Hey ${brand.agentName}", "OK Nabu").`,
      voiceDesc: 'Select your preferred voice for the agent responses.',
      autoListen: 'Auto Listen',
      autoListenDesc: 'Automatically start listening after the agent responds, allowing for continuous conversation.',
      confirmActions: 'Confirm Actions',
      confirmActionsDesc: 'Ask for user confirmation before executing automated commands.',
      settings: 'Settings',
      saving: 'Saving...',
      voiceServices: 'Voice Services',
      browserServices: 'Browser & Automation',
    },

    settingsDevices: {
      searchDevices: 'Search devices...',
    },

    settingsDangerZone: {
      dangerZone: 'Danger Zone',
      deleteAllRooms: 'Delete All Rooms',
      deleteAllRoomsDesc: 'Permanently delete all rooms and their configurations. This action cannot be undone.',
      deleteAllDevices: 'Delete All Devices',
      deleteAllDevicesDesc: 'Permanently delete all devices and their settings. This action cannot be undone.',
      deleteAllIntegrations: 'Delete All Integrations',
      deleteAllIntegrationsDesc: 'Disconnect and delete all integrations. This action cannot be undone.',
      deleteAllScenes: 'Delete All Scenes',
      deleteAllScenesDesc: 'Permanently delete all scenes and automations. This action cannot be undone.',
      factoryReset: 'Factory Reset',
      warning: 'Warning: This action cannot be undone.',
      resetting: 'Resetting...',
    },

    settingsLogs: {
      title: 'System Logs',
      logRetention: 'Log Cleanup',
      logRetentionDescription:
        'Automatically delete old records from log tables. Configure how many days of history to keep.',
      valueLogsRetention: 'Value Logs',
      scriptExecutionLogRetention: 'Script Execution Log',
      presenceEventsRetention: 'Presence Events',
      intercomCallsRetention: 'Intercom Calls',
      notificationsRetention: 'Notifications',
      eventsRetention: 'System Events',
      sceneExecutionsRetention: 'Scene Executions',
      messagesRetention: 'Messages',
      days: 'days',
      saveLogRetention: 'Save Cleanup Settings',
      cleanNow: 'Clean Now',
      cleaning: 'Cleaning...',
      cleanNowConfirm: 'Are you sure? Old records will be permanently deleted.',
      logRetentionSaved: 'Log cleanup settings saved',
      logCleanupDone: 'Cleanup complete: {count} records deleted',
      saving: 'Saving...',
    },

    settingsPlatform: {
      title: 'Platform',
      refresh: 'Refresh',
      refreshing: 'Refreshing...',
      daemon: 'Platform daemon',
      noUrlConfigured: 'No URL configured',
      version: 'Version',
      uptime: 'Uptime',
      profile: 'Profile',
      mode: 'Mode',
      mock: 'Mock',
      hardwareMode: 'Hardware',
      hardware: 'Hardware',
      gpioConfigured: 'GPIO configured',
      mockGpio: 'Mock GPIO',
      systemLed: 'System LED',
      fan: 'Fan',
      capabilities: 'Capabilities',
      ledState: 'LED state',
      apply: 'Apply',
      applying: 'Applying...',
      buttonPress: 'Button press',
      buttonShort: 'Short',
      buttonMedium: 'Medium',
      buttonLong: 'Long',
      buttonVeryLong: 'Very long',
      simulate: 'Simulate',
      simulating: 'Simulating...',
      host: 'Host',
      hostname: 'Hostname',
      runtime: 'Runtime',
      systemUptime: 'System uptime',
      memory: 'Memory',
      temperature: 'Temperature',
      loadAverage: 'Load average',
      dockerServices: 'Docker services',
      available: 'Available',
      unavailable: 'Unavailable',
      restart: 'Restart',
      restarting: 'Restarting...',
      noContainersReported: 'No containers reported.',
      power: 'Power',
      enabled: 'Enabled',
      disabled: 'Disabled',
      platform: 'Platform',
      command: 'Command',
      requesting: 'Requesting...',
      reboot: 'Reboot',
      shutdown: 'Shutdown',
      events: 'Events',
      noEventsReported: 'No events reported.',
      loadingStatus: 'Loading status',
      notConfigured: 'Not configured',
      connected: 'Connected',
      disconnected: 'Disconnected',
      none: 'None',
      na: 'N/A',
      noStatus: 'No status',
      of: 'of',
      running: 'running',
      unknown: 'Unknown',
      ledBooting: 'Booting',
      ledHealthy: 'Healthy',
      ledDegraded: 'Degraded',
      ledOffline: 'Offline',
      ledUpdating: 'Updating',
      ledError: 'Error',
      ledFactoryResetPending: 'Factory reset pending',
      ledUnknown: 'Unknown',
      confirmRestart: 'Restart {name}?',
      confirmReboot: 'Reboot the host?',
      confirmShutdown: 'Shut down the host?',
      saving: 'Saving...',
      storage: 'Storage',
      dockerEngine: 'Engine',
      dockerCli: 'CLI',
      hardwareDescription: 'Platform hardware profile and GPIO status',
      testHardware: 'Test hardware',
      hardwareTest: 'Hardware testing',
      hardwareTestDescription: 'Control the system LED and simulate button presses for testing.',
      ledCurrent: 'Current LED state',
      cancel: 'Cancel',
      close: 'Close',
      timeConfiguration: 'Time Configuration',
      timezone: 'Timezone',
      loadingTimezones: 'Loading timezones...',
      ntpServers: 'NTP Servers',
      ntpPlaceholder: 'ntp.example.com, pool.ntp.org',
      ntpDescription: 'Comma-separated list of NTP servers. Leave empty for default servers.',
      saveTimeSettings: 'Save Time Settings',
      timeSettingsSaved: 'Time settings saved successfully',
      monitoring: {
        title: 'System Monitoring',
        description:
          'Configure alerts for disk usage thresholds. Notifications are sent to admin users when limits are reached.',
        alerts: 'Alerts',
        diskWarning: 'Disk Warning Threshold',
        diskCritical: 'Disk Critical Threshold',
        save: 'Save Monitoring Settings',
        diskUsage: 'Disk',
        offlineNotice: 'Platform daemon is offline — thresholds may be local fallback values and cannot be saved.',
      },
      network: {
        title: 'Network',
        description: 'Network interfaces, WiFi and hostname management',
        hostname: 'Hostname',
        gateway: 'Default gateway',
        gatewayShort: 'GW',
        dns: 'DNS servers',
        backend: 'Backend',
        default: 'Default',
        noInterfaces: 'No interfaces reported.',
        connectSsid: 'WiFi SSID',
        ssidPlaceholder: 'Scan or type an SSID',
        password: 'Password',
        passwordPlaceholder: 'WiFi password',
        scan: 'Scan',
        scanning: 'Scanning...',
        connect: 'Connect',
        connecting: 'Connecting...',
        saveHostname: 'Save hostname',
        resetDhcp: 'Reset wired to DHCP',
        resetting: 'Resetting...',
        error: 'Error',
        confirmHostname: 'Change the hostname to {hostname}? This may affect mDNS access (e.g. {hostname}.local).',
        confirmResetDhcp: 'Reset the wired interface to DHCP? This may change the IP address.',
        configure: 'Configure',
        static: 'Static IP',
        dhcp: 'DHCP',
        methodLabel: 'Method',
        ipConfig: 'IP configuration',
        ipAddress: 'IP address',
        prefix: 'Prefix',
        dnsDescription: 'Comma-separated DNS servers.',
        dhcpDescription: 'The interface will obtain its IP address, gateway and DNS automatically from the network.',
        confirmStatic: 'Set {iface} to a static IP ({address})? The connection will be briefly interrupted.',
        confirmDhcp: 'Set {iface} to DHCP? The connection will be briefly interrupted.',
        wifi: 'WiFi',
        wired: 'Wired',
      },
      se: {
        title: 'Secure element',
        description: 'Hardware cryptographic co-processor for Matter certification',
        mode: 'Mode',
        type: 'Type',
        serial: 'Serial',
        i2C: 'I²C bus',
        provisioned: 'Provisioned',
        present: 'Present',
        notFound: 'Not found',
        yes: '✓ Yes',
        no: 'Not yet',
        error: 'Error',
      },
    },

    integrationCameras: {
      loadingState: 'Loading cameras...',
      camerasTab: 'Cameras',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
      nvrTab: 'NVR',
      statusConnected: 'Connected',
      statusReady: 'Ready',
      nvr: {
        serverSection: 'Share this hub (NVR server)',
        serverSectionHint: `Other ${brand.platformCompact} hubs can connect here and watch the cameras you assign to them.`,
        connectionInfo: 'Connection info',
        hubUrl: 'Hub URL (used as master URL on the other hub)',
        newClient: 'New client',
        clientName: 'Client name',
        createClient: 'Create',
        enabled: 'Enabled',
        disabled: 'Disabled',
        clientsTitle: 'Clients (other hubs)',
        noClients: 'No clients yet — create one to get a one-time token.',
        tokenTitle: 'One-time client token',
        tokenHint: 'Paste this token into the other hub when adding an NVR source. It is only shown once.',
        close: 'Close',
        assignedCameras: 'Assigned cameras',
        saveAssignment: 'Save cameras',
        lastSeen: 'Last seen',
        neverSeen: 'Never connected',
        deleteClient: 'Delete',
        remoteClient: 'Remote',
        sourceSection: 'Other hubs (NVR sources)',
        sourceSectionHint:
          'Add a hub that shares cameras with you, then import its cameras. Imported cameras are thin clients — recording happens on the source hub.',
        addSource: 'Add source',
        sourceName: 'Name',
        masterUrl: 'Hub URL',
        masterUrlHint:
          'Base URL of the other hub, e.g. https://my-hub. Its API is served under /api — both https://my-hub and https://my-hub/api are accepted.',
        token: 'Token',
        test: 'Test',
        testOk: 'Reachable',
        testFail: 'Failed',
        showCameras: 'Show cameras',
        importSelected: 'Import selected',
        noRemoteCameras: 'This hub has no cameras assigned to you yet.',
        remoteCamerasTitle: 'Cameras available on the source hub',
        importedTitle: 'Imported on this hub',
        noImported: 'No cameras imported yet.',
        remove: 'Remove',
        removeAll: 'Remove all',
        sync: 'Sync',
        adminRequired: 'Only admins can manage NVR sharing.',
        error: 'Failed to load NVR sharing settings.',
      },
    },

    integrationDatapoints: {
      backButton: 'Back to Integrations',
      pageTitle: 'Data Points',
      loadingState: 'Loading data points...',
      statusConnected: 'Connected',
      statusDisconnected: 'Disconnected',
      errorMessage: 'Failed to load data points',
    },

    integrationHomeAssistant: {
      entitiesTab: 'Entities',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
      loadingState: 'Loading Home Assistant data...',
      errorMessage: 'Failed to connect to Home Assistant',
    },

    integrationTorik: {
      entitiesTab: 'Import',
      devicesTab: 'Devices',
      settingsTab: 'Link',
      logsTab: 'Logs',
      loadingState: 'Loading Torik hub link...',
      entitiesTitle: 'Remote devices',
      entitiesHint:
        'Discover devices on the linked hub and import them as local devices. Values stay in sync and commands are forwarded.',
      discover: 'Discover devices',
      discovering: 'Discovering…',
      searchPlaceholder: 'Search by name or entity id…',
      selectAll: 'Select all',
      selected: 'selected',
      importCount: 'Import',
      imported: 'Imported',
      emptyState: 'Press Discover to fetch devices from the linked hub.',
      importTitle: 'Import remote devices',
      roomMapping: 'Map remote rooms to local rooms:',
      remoteRoom: 'Remote room:',
      noRoom: '— No room (auto-match by name) —',
      created: 'Created',
      skipped: 'Skipped',
      errors: 'Errors',
      close: 'Close',
      cancel: 'Cancel',
      import: 'Import',
      importing: 'Importing…',
      linkTitle: 'Hub link',
      linkHint: 'Connection to the linked Torik hub (its Torik API).',
      test: 'Test connection',
      testing: 'Testing…',
      sync: 'Sync now',
      syncing: 'Syncing…',
      syncDone: 'Sync complete',
      reconnect: 'Reconnect',
      reconnecting: 'Reconnecting…',
      reconnectDone: 'Reconnected',
      cleanup: 'Remove all imported devices',
      cleaning: 'Removing…',
      cleanupConfirm: 'Remove all devices imported from this hub? This cannot be undone.',
      cleanupDone: 'Removed devices',
      peerUrl: 'Peer URL',
      peerVersion: 'Peer version',
      token: 'Access token',
    },

    settingsTorikApi: {
      title: 'Torik API',
      subtitle: 'Home-Assistant-compatible REST + WebSocket API for external tools and hub-to-hub links.',
      refresh: 'Refresh',
      running: 'API server running',
      stopped: 'API server stopped',
      port: 'Port',
      clients: 'Connected clients',
      tokenCount: 'Active tokens',
      enabled: 'Enabled',
      enabledToast: 'Torik API enabled',
      disabledToast: 'Torik API disabled',
      endpoints: 'Endpoints',
      authHint: `Authenticate with "Authorization: Bearer <token>" (WebSocket: {'{'}"type":"auth","access_token":"<token>"{'}'}).`,
      tokensTitle: 'Access tokens',
      tokensHint: 'Long-lived bearer tokens. Create one here and paste it into the linking hub.',
      newToken: 'New token',
      noTokens: 'No API tokens yet.',
      lastUsed: 'last used',
      never: 'never',
      active: 'Active',
      disabled: 'Disabled',
      delete: 'Delete',
      deleteConfirm: 'Delete this token? Clients using it lose access immediately.',
      deleted: 'Token deleted',
      createTitle: 'Create API token',
      tokenCreatedTitle: 'Token created',
      tokenName: 'Token name',
      tokenNamePlaceholder: 'e.g. Secondary hub, Node-RED',
      cancel: 'Cancel',
      creating: 'Creating…',
      create: 'Create',
      copyWarning: 'Copy this token now — it is shown only once.',
      copy: 'Copy',
      copied: 'Token copied',
      copyFailed: 'Copy failed',
      done: 'Done',
    },

    integrationESPHome: {
      entitiesTab: 'Nodes & Entities',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
      loadingState: 'Loading ESPHome data...',
      errorMessage: 'Failed to load ESPHome integration',
    },

    integrationKNX: {
      statusDisabled: 'Disabled',
      statusConnected: 'Connected',
      statusDisconnected: 'Disconnected',
      statusNotConfigured: 'Not configured',
      configIssueTitle: 'Integration not configured',
      configIssueKnxMissingAddress:
        'The KNX gateway address is not set, so the integration cannot connect. Enter the IP address of your KNX IP interface under Connection Settings and save.',
      configIssueGeneric: 'Required connection settings are missing. Complete the settings below and save.',
      configIssuePaused: 'Automatic reconnects are paused until the settings are saved.',
      missingAddressWarning: 'No gateway address is set — KNX cannot connect until you enter the address and save.',
      // Serial TP-UART2 (card t_1f38250b): transport picker, port list, UART and advanced options
      serialTitle: 'Serial TP-UART2',
      serialTransportLabel: 'Serial Transport',
      serialTransportDirect: 'Direct on this device',
      serialTransportDirectHint: 'The hub opens the serial port itself and speaks TP-UART2 directly. Recommended.',
      serialTransportDaemon: 'Via platform daemon',
      serialTransportDaemonHint: 'Keep otto-platformd as the serial transport (compatibility mode).',
      serialTransportOwnershipHint:
        'Only one process can own the serial port: with Direct selected, the KNX TP-UART transport in otto-platformd must be OFF on this box.',
      serialPortLabel: 'Serial Port',
      serialPortRefresh: 'Refresh',
      serialPortsLoading: 'Loading serial ports…',
      serialPortsUnavailable: 'The hub did not list serial ports — enter the device path manually.',
      serialPortsEmpty: 'No serial ports were found — enter the device path manually.',
      serialPortInUse: 'in use',
      serialBaudRateLabel: 'Baud Rate',
      serialParityLabel: 'Parity',
      serialParityNone: 'None',
      serialParityEven: 'Even',
      serialParityOdd: 'Odd',
      serialDataBitsLabel: 'Data Bits',
      serialStopBitsLabel: 'Stop Bits',
      serialCrc16Label: 'CRC16 Telegram Check',
      serialCrc16Hint: 'Append and verify CRC16 on received telegrams. Available at 19200 baud only.',
      serialAdvancedTitle: 'Advanced',
      serialEopTimeoutLabel: 'EOP Timeout (ms)',
      serialEopTimeoutHint:
        'End-of-packet silence that delimits telegrams. The TP-UART2 datasheet specifies 2–2.5 ms — keep the default 2.5.',
      serialMaxSendRetriesLabel: 'Send Retries',
      serialMaxSendRetriesHint: 'How many times a telegram is retransmitted when the chip does not confirm it.',
      serialKeepAliveLabel: 'Keep-Alive Interval (ms)',
      serialKeepAliveHint: 'How often the chip link state is polled to keep the serial connection alive.',
      serialCommandTimeoutLabel: 'Command Timeout (ms)',
      serialCommandTimeoutHint: 'How long to wait for a chip confirmation before retrying a telegram.',
      serialPhysicalAddressRequired: 'A physical address is required for serial (TP-UART2) connections.',
      // KNX/IP interface (card t_17c8d635): enabled in serial mode, with a live status readout
      ipServerTitle: 'KNX/IP Gateway Server',
      ipServerEnableLabel: 'Enable IP Tunnel Server',
      ipServerEnableHint:
        '{agent} listens as a KNXnet/IP server so other clients (ETS, visualisations) can connect to it.',
      ipServerListenPortLabel: 'Listen Port',
      ipServerMaxSessionsLabel: 'Max Sessions (optional)',
      ipServerEtsHint:
        'The hub serves a full KNX/IP tunnel on this port — ETS can connect, monitor the bus and program devices through it. Works in the tunnel modes and in serial (direct TP-UART2) mode.',
      ipServerStatusTitle: 'Interface Status',
      ipServerStatusRefresh: 'Refresh',
      ipServerStatusChecking: 'Checking…',
      ipServerStatusListening: 'Listening',
      ipServerStatusNotListening: 'Not listening',
      ipServerStatusUnavailable: 'Status unavailable',
      ipServerStatusNotListeningHint:
        'The hub reports the interface is not listening. Save the settings and make sure the KNX integration is connected.',
      ipServerStatusUnavailableHint:
        'This hub did not answer the interface status request, so its state cannot be shown here.',
      ipServerSessionClient: 'Client',
      ipServerSessionChannel: 'Channel',
      ipServerSessionSince: 'Since',
      ipServerNoSessions: 'No clients are connected right now.',
      // Remote integrator access (card t_d51acc6a): the desktop-app flow + the live session readout
      remoteAccessTitle: 'Remote ETS Access',
      remoteAccessIntro:
        'The integrator runs the otto-knx-link desktop app, signs in to the Torik cloud and connects to this hub through an active remote-support session — then ETS works with this bus through a local interface on their PC. No inbound port is opened on this site.',
      remoteAccessHowTitle: 'How it works',
      remoteAccessHowStep1: 'Install the otto-knx-link desktop app on the integrator’s PC (Windows).',
      remoteAccessHowStep2: 'Sign in with the integrator’s Torik cloud account.',
      remoteAccessHowStep3: 'Connect the app to this hub — access rides an active remote-support session.',
      remoteAccessHowStep4: 'In ETS, connect to the KNXnet/IP interface at {address}.',
      remoteAccessEtsAddressLabel: 'ETS connection address',
      remoteAccessDownloadPending: 'A download link for the app will appear here once its first release is published.',
      remoteAccessPrereqTitle: 'Before you start',
      remoteAccessPrereqHub: 'This hub is online and the KNX integration is connected.',
      remoteAccessPrereqHubNotConnected:
        'The KNX integration is not connected right now — ETS would reach this hub, but not the bus.',
      remoteAccessPrereqHubDisabled: 'The KNX integration is disabled.',
      remoteAccessPrereqSessionActive: 'An active remote-support session grants access ({name}).',
      remoteAccessPrereqSessionNone:
        'No session is active. Create one under Settings → Remote Support and pick this integration.',
      remoteAccessPrereqWindow: 'The session window is open — it closes in {remaining}.',
      remoteAccessPrereqWindowClosed: 'No session window is open.',
      remoteAccessWhoTitle: 'Who has access right now',
      remoteAccessWhoDesc:
        'A session grants the named installer access to this integration’s bus while its window is open. Closing the session closes the link.',
      remoteAccessWhoEmpty: 'No active remote-support session on this integration.',
      remoteAccessWhoCreatedBy: 'Created by {name}',
      remoteAccessWhoExpiresIn: 'closes in {remaining}',
      remoteAccessManage: 'Manage remote support',
      remoteAccessRefresh: 'Refresh',
      remoteAccessLoading: 'Loading…',
      remoteAccessUnavailable: 'This hub did not answer the session list, so remote access cannot be checked here.',
      remoteAccessNoInstaller: 'No installer assigned',
      deleteAllDevicesButton: 'Delete All Devices',
      loadingState: 'Loading KNX data...',
      settingsTab: 'Settings',
      datapointsTab: 'Data Points',
      devicesTab: 'Devices',
      groupMonitorTab: 'Group Monitor',
      remoteAccessTab: 'Remote Access',
      logsTab: 'Logs',
      deleteModalTitle: 'Delete All KNX Devices',
      deleteConfirmMessage: 'Are you sure you want to delete all KNX devices? This action cannot be undone.',
      deleteConfirmLabel: 'To confirm, type "DELETE" below:',
      deleteConfirmPlaceholder: 'Type DELETE to confirm',
      cancelButton: 'Cancel',
      deleteConfirmButton: 'Delete All Devices',
      deletingState: 'Deleting devices...',
      errorMessage: 'Failed to process KNX request',
    },

    remoteSupport: {
      selectIntegration: 'Select KNX Integration',
      selectIntegrationPlaceholder: 'Choose a KNX integration...',
      knxRemoteSupport: 'KNX Remote Support',
      tpuartTitle: 'TP-UART Driver Required',
      tpuartDesc:
        'KNX interface sharing via TP-UART driver will be available when the platform daemon supports it. The hub will then be able to share its physical KNX interface with remote support users.',
      remoteUsers: 'Remote Support Users',
      remoteUsersDesc:
        'Manage users who can provide remote support. Add users by email and configure their access time limits.',
      addUserByEmail: 'Add User by Email',
      emailPlaceholder: "user{'@'}example.com",
      addUser: 'Add',
      adding: 'Adding...',
      remove: 'Remove',
      removing: 'Removing...',
      installerRole: 'Installer',
      noEmail: 'No email',
      noRemoteUsers: 'No remote support users added yet.',
      cannotAddSelf: 'You cannot add yourself. This would lock you out of your admin account.',
      editAccess: 'Edit',
      day: 'Day',
      days: 'Days',
      daysAccess: 'days access',
      perSession: 'per session',
      unlimited: 'Unlimited',
      unlimitedAccess: 'Unlimited access',
      confirmRemoveUser: 'Remove {name} from remote support? Their account will be converted back to a regular user.',
      accessDaysPrompt: 'Access duration (days, 0 = unlimited):',
      accessHoursPrompt: 'Max hours per session:',
      issueReport: 'Issue Report / Chat',
      issueReportDesc: 'Message the remote support user about the issue being worked on.',
      viewChat: 'Chat',
      activeSessions: 'Active Sessions',
      newSession: 'New Remote Support Session',
      selectUser: 'Select Remote User',
      selectUserPlaceholder: 'Choose a user...',
      timeWindow: 'Session Duration',
      createSession: 'Create Session',
      creating: 'Creating...',
      endSession: 'End Session',
      ending: 'Ending...',
      installerLabel: 'Installer',
      timeRemaining: 'Time Remaining',
      expired: 'Expired',
      justNow: 'just now',
      ago: 'ago',
      hour: 'Hour',
      hours: 'Hours',
      minutes: 'Minutes',
      send: 'Send',
      messagePlaceholder: 'Type a message...',
      noMessages: 'No messages yet. Start the conversation.',
    },

    integrationMatter: {
      loadingState: 'Loading Matter data...',
      settingsTab: 'Settings',
      nodesTab: 'Nodes',
      devicesTab: 'Devices',
      logsTab: 'Logs',
      errorMessage: 'Failed to connect to Matter network',
    },

    integrationMatterBridge: {
      brideDeviceCount: 'Bridged Devices',
      loadingState: 'Loading Matter Bridge data...',
      bridgedDevicesTab: 'Bridged Devices',
      commissioningTab: 'Commissioning',
      settingsTab: 'Settings',
      logsTab: 'Logs',
      errorMessage: 'Failed to connect to Matter Bridge',
    },

    integrationComputers: {
      subtitle: `Computers running the ${brand.agentName} agent, paired with this hub.`,
      integrationLabel: `${brand.platformCompact} Computers`,
      refreshAction: 'Refresh',
      addComputerAction: 'Add Computer',
      totalLabel: 'Computers',
      onlineLabel: 'Online',
      offlineLabel: 'Offline',
      onlineState: 'Online',
      offlineState: 'Offline',
      connectedState: 'Connected',
      noComputersState: 'Enabled, no computers',
      disabledState: 'Disabled',
      unknownState: 'Unknown',
      loadingState: 'Loading computers…',
      emptyTitle: 'No computers yet',
      emptyMessage: `Install the ${brand.agentName} agent on a computer and pair it with this hub to control it from ${brand.platformName}.`,
      hostLabel: 'Host',
      platformLabel: 'Platform',
      macLabel: 'MAC',
      agentVersionLabel: 'Agent',
      lastSeenLabel: 'Last seen',
      neverSeen: 'never',
      memoryLabel: 'Memory',
      diskLabel: 'Disk',
      tempLabel: 'CPU temp',
      uptimeLabel: 'Uptime',
      rebootAction: 'Reboot',
      sleepAction: 'Sleep',
      shutdownAction: 'Shutdown',
      wakeAction: 'Wake',
      wakeSendAction: 'Send wake packet',
      wakeHint: 'The hub sends the wake packet from its own network (WOL must be enabled in the computer BIOS).',
      removeAction: 'Remove',
      refreshNodeAction: 'Refresh status',
      macPlaceholder: 'AA:BB:CC:DD:EE:FF',
      loadError: 'Failed to load computers',
      refreshFailed: 'Refresh failed',
      powerSent: 'Power command sent to',
      powerFailed: 'Power command failed',
      wakeSent: 'Wake packet sent',
      wakeFailed: 'Wake-on-LAN failed',
      removeConfirmTitle: 'Remove computer?',
      removeConfirmMessage: 'This unlinks the computer from this hub and removes its device.',
      removed: 'Computer removed',
      removeFailed: 'Remove failed',
      addDialogTitle: 'Add Computer',
      addDialogSubtitle: `Generate a pairing code, then install the ${brand.agentName} agent on the computer with this code. The code expires after 15 minutes.`,
      nodeNamePlaceholder: 'e.g. Studio PC',
      generateTokenAction: 'Generate pairing code',
      copyTokenAction: 'Copy',
      pairingCodeLabel: 'Pairing code',
      hubAddressLabel: 'Hub address',
      hubAddressHint:
        'The address computers will use to reach this hub. The list shows this hub’s network addresses — pick the one the computer can reach (e.g. the same network), or enter a custom address. In production (https) the hub address is pre-selected.',
      installCommandTitle: 'On the computer, run:',
      doneAction: 'Done',
      copied: 'Pairing code copied',
      commandCopied: 'Command copied',
      copyFailed: 'Copy failed',
      computersTab: 'Computers',
      devicesTab: 'Devices',
      customHubAddressAction: 'Custom address…',
      customHubAddressPlaceholder: 'http://192.168.1.72',
      selectHubAddressHint: 'Pick a hub address above to see the install commands.',
      pairingFailed: 'Failed to generate pairing code',
    },
    integrationTouchPanels: {
      integrationName: 'Touch Panels',
      statusDisabled: 'Disabled',
      statusConnected: 'Connected',
      statusReady: 'Ready',
      loadingState: 'Loading Touch Panels integration...',
      panelsTab: 'Panels',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
      refreshAction: 'Refresh',
      panelsAction: 'Panels',
      settingsAction: 'Settings',
      logsAction: 'Logs',
      totalPanelsLabel: 'Registered Panels',
      onlinePanelsLabel: 'Online Now',
      offlinePanelsLabel: 'Offline',
      sleepingPanelsLabel: 'Sleeping',
      pairingStatusLabel: 'Pairing Status',
      pairedPanelsLabel: 'Paired',
      pendingPanelsLabel: 'Pending Pairing',
      lastActivityLabel: 'Latest Activity',
      noActivityLabel: 'No recent activity',
      noActivityDescription: 'No panel has reported activity yet',
      attentionLabel: 'Needs Attention',
      justNowLabel: 'just now',
      minutesAgoLabel: '{count}m ago',
      hoursAgoLabel: '{count}h ago',
      daysAgoLabel: '{count}d ago',
      errorMessage: 'Failed to load Touch Panels integration',
    },

    integrationZigbee: {
      loadingState: 'Loading Zigbee data...',
      nodesTab: 'Nodes',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      errorMessage: 'Failed to connect to Zigbee network',
    },

    integrationZWave: {
      loadingState: 'Loading Z-Wave data...',
      nodesTab: 'Nodes',
      networkTab: 'Network',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      errorMessage: 'Failed to connect to Z-Wave network',
    },
    integrationWled: {
      segmentsTab: 'Segments',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
      loadingState: 'Loading...',
      errorMessage: 'Failed to load integration',
    },

    integrationHue: {
      lightsTab: 'Lights',
      groupsTab: 'Groups',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
    },
    integrationUnifi: {
      networkTab: 'Network',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
    },
    integrationSomfy: {
      gatewayTab: 'Gateway',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
    },
    integrationSynology: {
      statusTab: 'Status',
      devicesTab: 'Devices',
      settingsTab: 'Settings',
      logsTab: 'Logs',
    },
    integrationMediaPlayers: {
      playersTab: 'Players',
      devicesTab: 'Devices',
      sourcesTab: 'Sources',
      settingsTab: 'Settings',
    },
    integrationSonos: {
      settingsTab: 'Settings',
      playersTab: 'Players',
      devicesTab: 'Devices',
    },
    integrationSpotify: {
      settingsTab: 'Settings',
      devicesTab: 'Devices',
    },

    panelPairing: {
      title: `${brand.agentName} Touch Panel`,
      subtitle: 'Device Setup',
      discoverTitle: `Find your ${brand.agentName} Hub`,
      discoverDescription: `Searching your local network for an ${brand.agentName} Hub…`,
      scanningStatus: 'Scanning network…',
      noHubsFound: 'No hubs found automatically.',
      manualEntryHint: 'Enter the hub address manually below.',
      manualEntryLabel: 'Or enter manually',
      ipAddressPlaceholder: 'Hub IP address',
      portPlaceholder: 'Port',
      connectButton: 'Connect',
      rescanButton: '↻ Scan again',
      backButton: 'Back',
      connectedToLabel: 'Connected to',
      pairingMethodTitle: 'Pair this device',
      pairingRequirement: `An admin must generate a pairing code in the ${brand.agentName} app first.`,
      enterPinButton: 'Enter PIN',
      pinEntryTitle: 'Enter Pairing PIN',
      pinEntryDescription: `Enter the 6-digit PIN shown in the ${brand.agentName} admin app.`,
      pairDeviceButton: 'Pair Device',
      pairingInProgress: 'Pairing…',
      invalidPinError: 'Invalid PIN. Please try again.',
      cameraAccessDenied: 'Camera access denied. Please enable camera permissions.',
      invalidQrError: 'Invalid QR code. Please try again.',
      scanQrButton: 'Scan a pairing code',
      scanQrTitle: 'Scan the code',
      qrHint:
        'Point the camera at the code shown in the app — a panel pairing code, or a device code from Connect a device.',
      qrScanning: 'Looking for a code…',
      exitPinTitle: 'Set Exit PIN',
      exitPinDescription: 'Choose a 4-digit PIN to exit panel mode later.',
      skipButton: 'Skip',
      pairedSuccess: 'Device Paired!',
      connectingMessage: 'Connecting to your panel…',
    },

    playPairing: {
      title: `${brand.agentName} Play`,
      subtitle: 'Device Setup',
      discoverTitle: `Find your ${brand.agentName} Hub`,
      discoverDescription: `Searching your local network for an ${brand.agentName} Hub…`,
      scanningStatus: 'Scanning network…',
      noHubsFound: 'No hubs found automatically.',
      manualEntryHint: 'Enter the hub address manually below.',
      manualEntryLabel: 'Or enter manually',
      ipAddressPlaceholder: 'Hub IP address',
      portPlaceholder: 'Port',
      connectButton: 'Connect',
      rescanButton: '↻ Scan again',
      backButton: 'Back',
      connectedToLabel: 'Connected to',
      pairingMethodTitle: 'Pair this device',
      pairingRequirement: `An admin must generate a pairing code in the ${brand.agentName} app first.`,
      enterPinOption: 'Enter PIN',
      enterPinDescription: 'Enter the 6-digit code shown in the admin app',
      scanQrOption: 'Scan QR Code',
      scanQrDescription: 'Scan the QR code shown in the admin app',
      pinEntryTitle: 'Enter Pairing PIN',
      pinEntryDescription: `Enter the 6-digit PIN shown in the ${brand.agentName} admin app.`,
      pairDeviceButton: 'Pair Device',
      pairingInProgress: 'Pairing…',
      scanQrTitle: 'Scan QR Code',
      scanQrInstruction: `Point the camera at the QR code in the ${brand.agentName} admin app.`,
      scanningAutoStatus: 'Scanning automatically…',
      exitPinTitle: 'Set Exit PIN',
      exitPinDescription: 'Choose a 4-digit PIN to exit play mode later.',
      confirmPinButton: 'Confirm PIN',
      reenterPinInstruction: 'Re-enter your PIN to confirm',
      pinMismatchError: "PINs don't match. Try again.",
      skipButton: 'Skip',
      pairedSuccess: 'Device Paired!',
      pairedDescription: `This device is now linked to your ${brand.agentName} hub as a Play device.`,
      startPlayModeButton: 'Start Play Mode',
      invalidPinError: 'Invalid PIN. Please try again.',
      cameraAccessDenied: 'Camera access denied. Please enable camera permissions.',
      invalidQrError: 'Invalid QR code. Please try again.',
    },

    gallery: {
      pageTitle: 'Photos',
      emptyTitle: 'No photos yet',
      emptyDescription: 'Upload photos and videos to start your personal cloud gallery.',
      noAlbumTitle: 'No albums yet',
      noAlbumDescription: 'Create an album to group and share your photos.',
      noAlbumItems: 'No photos in this album yet',
      newAlbum: 'New Album',
      takePhoto: 'Camera',
      uploadPhotos: 'Upload',
      createAlbumTitle: 'Create Album',
      selectPhotos: 'Select',
      uploadProgress: 'Uploading',
      dropZoneText: 'Drop photos & videos here',
      uploadStatus: 'file',
      uploadStatusPlural: 'files',
      sharedBadge: 'Shared',
      publicBadge: 'Public',
    },

    reminders: {
      pageTitle: 'Reminders',
      addButton: 'New Reminder',
      upcomingTab: 'Upcoming',
      completedTab: 'Completed',
      emptyUpcoming: 'No upcoming reminders.',
      emptyCompleted: 'No completed reminders.',
      createReminderTitle: 'New Reminder',
      editReminderTitle: 'Edit Reminder',
      titleLabel: 'Title',
      titlePlaceholder: 'Pick up groceries',
      notesLabel: 'Notes',
      notesPlaceholder: 'Extra details…',
      notesOptional: '(optional)',
      dateTimeLabel: 'Date & Time',
      repeatLabel: 'Repeat',
      repeatNone: 'None',
      repeatDaily: 'Daily',
      repeatWeekly: 'Weekly',
      repeatMonthly: 'Monthly',
      repeatWeekdays: 'Weekdays (Mon–Fri)',
      cancelButton: 'Cancel',
      saveButton: 'Save',
      createButton: 'Create Reminder',
    },

    timers: {
      pageTitle: 'Timers',
      addButton: 'New Timer',
      emptyState: 'No timers yet. Tap New Timer to create one.',
      createTimerTitle: 'New Timer',
      labelLabel: 'Label',
      labelPlaceholder: 'Pasta water',
      durationLabel: 'Duration',
      hoursLabel: 'Hours',
      minutesLabel: 'Minutes',
      secondsLabel: 'Seconds',
      soundLabel: 'Sound',
      totalLabel: 'total',
      startButton: 'Play',
      pauseButton: 'Pause',
      resetButton: 'Reset',
      deleteButton: 'Delete',
      deleteConfirm: 'Delete this timer?',
      cancelButton: 'Cancel',
      createButton: 'Create',
      creatingLabel: 'Creating…',
      statusIdle: 'idle',
      statusRunning: 'running',
      statusPaused: 'paused',
      statusCompleted: 'completed',
    },

    // The Local page (`views/LocalServers.vue`): the hubs found on this network
    // and the ones saved on this device. New strings sit here; the page's older
    // copy is written in its own inline-English style (`utils/consoleText.ts`).
    //
    // There is no `join`/`joinHint`/`joinUnsupported` here any more: the join
    // button and its code surface left this page (card `t_c890ce4c`, D3). What
    // is left is what a card with no way in says about the hub it is looking at.
    //
    // …and no `ownedByAnother` either (plan `LOCAL-FIRST-CONNECT.md` §2/D3,
    // 2026-09-24). It read "{hub} belongs to someone else — ask its owner…",
    // which the app never established: it is chosen from *local* facts alone
    // (the hub named itself, it is not claimable, this device holds nothing) and
    // it told the owner his own hub belonged to a stranger. Third-party
    // ownership is not a fact this surface can know — only its own side of it.
    // The guest experience of the demo house (card `t_803e86a4`): the chip that
    // names the visitor in the top bar, and the one-line honesty strip under the
    // bar. The welcome page (D10) writes `demo_guest` before it redirects; the
    // app reads it (`composables/useDemoGuest.ts`) and the role `viewer` decides.
    demoGuest: {
      pill: 'Guest',
      pillNamed: 'Guest · {name}',
      hint: 'This is a demo home — a full day is simulated in minutes.',
      dismiss: 'Dismiss',
    },
    localServers: {
      hubNotIdentified: 'Did not identify itself (older software)',
      unclaimedHere: 'Nobody owns {hub} yet — open the Torik app to adopt it.',
      holdsNothingFor:
        '{hub} is reachable, and this device holds nothing that opens it. If your Torik account is on this hub, open it from your servers — otherwise ask an administrator to add your account.',
    },
    // The /home launcher's own local connect (card `t_ce03901f`): it offers the
    // same ways in the Local page does and paints the same sentences for the
    // states it cannot act on (`localServers` above). This is the one line the
    // two surfaces do not share.
    launcher: {
      localHint:
        'This hub opens with your Torik account — on this network when it answers here, through the secure tunnel when it does not. A device credential stored on this device opens it too; no hub password is asked for, and none is stored.',
    },
  },
  shell: {
    home: 'Home',
    // The floor for a tab nothing else names: a chip the app inside it has not
    // titled yet (card t_99f5ed79). The main process paints it — Electron has no
    // catalogue of its own, so the bar registers this word with the rest.
    newTab: 'New tab',
    closeTab: 'Close tab',
    alertError: 'Error',
    alertWarning: 'Warning',
    alertNotice: 'Alert',
    player: 'Player',
    previous: 'Previous',
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
    volume: 'Volume',
    // The words the **main process** paints in its own native popups (card
    // t_99f5ed79): the tab context menu, the app menu, and the connection-info
    // dialog. Electron has no catalogue of its own, so the bar registers these
    // (`shell/shellLabels.ts` → `electron/shellMenus.ts`); `menu`/`info` are the
    // popup and dialog groups of that payload.
    openInNewWindow: 'Open in new window',
    connectionInfo: 'Connection info',
    reload: 'Reload',
    fullscreen: 'Fullscreen',
    close: 'Close',
    launcher: 'Launcher',
    services: 'Services',
    miniPlayerWindow: 'Mini player window',
    rebuildBar: 'Rebuild system bar',
    settings: 'Settings',
    quit: 'Quit',
    info: {
      target: 'Target',
      endpoint: 'Endpoint',
      hubId: 'Hub id',
      hubName: 'Hub name',
      state: 'State',
      route: 'Route',
      connected: 'connected',
      disconnected: 'disconnected',
      secure: 'secure',
      cloudHub: 'cloud hub {id}',
      managedInLauncher: 'Credentials and saved servers are managed in the launcher.',
      ok: 'OK',
      tab: 'Tab',
    },
  },
  connectionLost: {
    title: 'Connection lost',
    session: '{hub}: your session expired. Reconnect to keep working in this tab.',
    offline: '{hub} is not responding. Check the network, then reconnect.',
    thisServer: 'This server',
    reconnect: 'Reconnect',
    reconnecting: 'Reconnecting…',
    close: 'Close',
  },
  api: {
    // What a failed hub call actually means (`utils/apiFailure.ts`), so a surface
    // that only catches the error can still name the cause instead of one sentence
    // that fits every failure — read as `t(apiFailureSentenceKey(error))`.
    failure: {
      unreachable: 'The hub could not be reached, so these settings could not be read.',
      refused: 'This window is not signed in to the hub, so it refused these settings.',
      wrong_target:
        'The reply came from somewhere other than this hub’s own service, so these settings could not be read.',
      hub_failed: 'The hub failed while reading these settings.',
    },
  },
  hub: {
    // Adopting an unclaimed hub from the app (HEADLESS-HUB-ONBOARDING.md
    // D3/D4/D5): the confirm sheet, the sticker QR and every refusal the hub
    // can answer with — one sentence each, in every shipped locale.
    adopt: {
      title: 'Adopt this hub',
      intro:
        'This hub is on your network and has no owner yet. Adopting registers it under your Torik account and makes you its owner.',
      // The hub's own answer was not `true` (plan `LOCAL-FIRST-CONNECT.md`
      // §2/D3): it is claimed, or it did not say. Says what happened and the
      // ways in that exist — never who owns it, which this app cannot know.
      notOffered:
        'This hub is not waiting for adoption, so there is nothing to adopt here. Nothing has been sent — if your Torik account is on this hub, open it from your servers.',
      hubLabel: 'Hub',
      accountLabel: 'Account',
      roleLabel: 'Your role',
      roleOwner: 'Owner',
      noCode: 'Nothing to type — your account is the credential.',
      noAccount: 'Not signed in',
      // A session whose account this sheet holds no name for: the device IS
      // signed in, and the row must not say otherwise (card t_a53c5f61).
      accountSignedIn: 'Signed in',
      signInRequired: 'Sign in to your Torik account to adopt this hub.',
      signIn: 'Sign in',
      signingIn: 'Opening…',
      signInFailed: 'The sign-in could not be started. Try again.',
      confirm: 'Adopt this hub',
      working: 'Adopting…',
      retry: 'Try again',
      cancel: 'Cancel',
      doneTitle: '{hub} is yours',
      doneBody: 'It is registered under {account}, and this device is now paired with it.',
      // The unnamed-account variant of the sentence above: `{account}` cannot
      // carry a phrase in every language, so this case re-words itself.
      doneBodySignedIn: 'It is registered under your Torik account, and this device is now paired with it.',
      doneWhere: 'You will find it in your servers, on the home page.',
      roleLine: 'You hold the {role} role on this hub.',
      continue: 'Continue',
      scanLead: 'Brand-new hub? Scan the code printed on its box — that adopts it under your account.',
      linkWrongHub: 'That code belongs to a different hub than the one answering at that address. Nothing was adopted.',
      linkUnverified:
        'The hub at that address did not confirm which hub it is, so nothing was adopted. Check that the hub is switched on and reachable.',
      rateLimited: 'Too many attempts from this device. Wait a minute, then try again.',
      notOnHubNetwork:
        'The hub refused: this device is not on the hub’s own network. Connect to the same network as the hub (not a VPN) and try again.',
      alreadyClaimed: 'This hub already has an owner. Ask them to invite you instead.',
      invalidCloudToken: 'Your Torik account session was refused. Sign in again, then adopt.',
      invalidDevice: 'The hub refused this device’s key. Try again.',
      cloudUnreachable:
        'The hub could not reach the Torik cloud, so it could not register itself. Check the hub’s internet connection and try again.',
      hubIdentityUnavailable: 'The hub cannot read its own identity yet. Wait a moment, then try again.',
      registrationFailed: 'The cloud refused to register the hub under your account. Try again, or contact support.',
      unsupported: 'This hub does not support adoption (older software).',
      unreachable: 'Nothing answered at that address. Check that the hub is switched on and on this network.',
      unproven:
        'The device at that address could not prove it is this hub. Nothing was adopted and your account was not sent to it.',
      malformed: 'The hub answered without an adoption. Nothing was changed.',
      refused: 'The hub refused the adoption.',
      cardAction: 'Unclaimed hub — adopt it?',
      cardHint: 'Nobody owns {hub} yet. Adopting registers it under your Torik account and makes you its owner.',
      cardSignIn: 'Sign in to your Torik account to adopt {hub}.',
      badgeWaiting: 'Waiting for Adoption',
      // Adoption is the find surface's action (plan `LOCAL-FIRST-CONNECT.md` §2/D3):
      // the launcher's connect dialog states the hub's state and carries exactly
      // one link to the surface that can actually claim it — the find-local-servers
      // modal (`components/server/FindLocalServersModal.vue`, plan
      // `HOME-ONLY-SERVER-CARDS.md` §3 C), where the hub's own facts are on screen
      // next to the Adopt button.
      localPageHint: 'Nobody owns {hub} yet — it is not in your servers: find it under Find local servers.',
      localPageAction: 'Find local servers',
      addressLabel: 'Address',
      loopbackOnly:
        'This device can only reach this hub at {address} — the hub’s own machine. A hub never accepts an adoption from itself (tunnelled calls arrive from there too), so adopt it from another device on the same network.',
      tokenRetired:
        'The hub no longer accepts an account sign-in for adoption — a licence is required. Update the app, then try again.',
      licenceUsed: 'That adoption licence was already spent. Nothing was changed — try again to get a fresh one.',
      licenceExpired: 'The adoption licence expired before the hub could use it. Try again.',
      licenceMismatch: 'That licence was issued for a different hub. Nothing was adopted.',
      licenceInvalid:
        'The hub could not verify the licence the cloud signed. Update the app and the hub, then try again.',
      licenceRequired: 'The hub expected an adoption licence and none was sent. Update the app, then try again.',
      licenceRefused: 'The cloud refused to issue an adoption licence for this hub.',
      licenceUnknownHub:
        'The cloud could not identify this hub, so it issued no licence. Let the hub reach the internet once, then try again.',
      licenceAlreadyOwned: 'Another Torik account already owns this hub. Ask its owner to invite you instead.',
      licenceUnavailable: 'The cloud cannot issue adoption licences right now. Try again later, or contact support.',
      licenceUnauthorized: 'Your Torik account session was refused. Sign in again, then adopt.',
      licenceUnreachable:
        'The Torik cloud could not be reached, so no licence could be issued. Check this device’s internet connection and try again.',
      licenceMalformed: 'The cloud answered without a licence. Nothing was adopted — try again.',
      password: {
        title: 'Set a local password',
        intro:
          'A password for {hub} keeps this house working without the internet — and lets anyone in the house sign in even when the Torik cloud is unreachable.',
        label: 'Password',
        hint: 'At least 8 characters.',
        confirmLabel: 'Confirm password',
        mismatch: 'The two passwords do not match.',
        currentLabel: 'Current password',
        save: 'Save password',
        saving: 'Saving…',
        skip: 'Not now',
        done: 'Local password set — this house now works without the internet.',
        noSessionNote:
          'This device is not signed in on the hub, so no local password could be set. Open the hub and set one there.',
        resolving: 'Checking this server…',
        noAddress: 'This server has no address this device can dial, so a local password cannot be set from here.',
        noAccount: 'Sign in to your Torik account to set a local password on this hub.',
        notThisAccount: 'This hub belongs to a different Torik account, so this account cannot set its local password.',
        noAnswer:
          'Nothing answered at this hub’s address, so no local password could be set. Check that the hub is switched on and on this network.',
        close: 'Close',
        required: 'Enter a password.',
        tooShort: 'The password is too short — at least 8 characters.',
        tooLong: 'The password is too long (72 bytes at most).',
        wrongCurrent: 'That is not the password this account already has on the hub.',
        notPermitted: 'Only an administrator can set this password.',
        noSuchUser: 'The hub does not know that account.',
        noSession: 'This device is not signed in on the hub, so it cannot set a password.',
        invalidCredentials: 'Wrong handle or password.',
        disabled: 'That account is disabled on this hub.',
        // The window, refused on the offline door too (card `t_2f0c4a91`) — and
        // worded apart from "disabled", which is a different fact.
        outsideWindow:
          'This account can only sign in inside the hours set on the hub. Ask the hub’s owner to move the window.',
        tooManyAttempts: 'Too many attempts. Wait a minute, then try again.',
        unreachable: 'Nothing answered at that address. Check that the hub is switched on and on this network.',
        refused: 'The hub refused to set that password.',
        // The **clear**'s own three (`services/hubLocalPassword.ts` →
        // `localPasswordClearMessageKey`): the sentences above that name a *set*
        // read as nonsense on a Remove button.
        removeNotPermitted: 'Only an administrator can remove another person’s local password.',
        removeNoSession: 'This device is not signed in on the hub, so it cannot remove a password.',
        removeRefused: 'The hub refused to remove that password.',
      },
    },
    join: {
      title: 'Join this hub',
      intro:
        'This hub is on your network and your Torik account already holds a role on it. Joining links your account here and lets this device use the hub — no code, no password.',
      hubLabel: 'Hub',
      accountLabel: 'Account',
      roleLabel: 'Your role',
      roleReading: 'Checking…',
      // No role to name yet — the link has not answered (or was refused). The
      // device-credential copy (`enroll.session.roleUnknown` → "a device") is
      // about a *session*, and saying it here would name the wrong thing.
      roleUnknown: 'Not known yet',
      noCode:
        'Nothing to type — your account is the credential. The code on the hub’s screen is for guests, contractors and panels without a keyboard.',
      noAccount: 'Not signed in',
      signInRequired: 'Sign in to your Torik account to join this hub.',
      confirm: 'Join this hub',
      working: 'Joining…',
      retry: 'Try again',
      cancel: 'Cancel',
      doneTitle: 'You have joined {hub}',
      doneBody: '{account} is now linked to this hub, and this device can use it.',
      roleLine: 'Your role on this hub is {role}.',
      continue: 'Continue',
      noMatchingUser:
        'This hub has no user with your email address yet. Ask the hub’s owner to invite this address, then try again.',
      notAMember: 'Your Torik account has no role on this hub. Ask the hub’s owner to invite this address.',
      userDisabled: 'This account is disabled on this hub. Ask the hub’s owner to enable it.',
      // The hours this person may sign in (card `t_2f0c4a91`): an installer is a
      // cloud user invited for a visit, and this is the refusal they meet when
      // the visit runs outside the window the owner set.
      outsideLoginWindow:
        'This account can only sign in inside the hours its owner set on the hub. Ask them to move the window.',
      invalidCloudToken: 'Your Torik account session was refused. Sign in again, then retry.',
      deviceInUse:
        'This device is already enrolled under another account. Revoke it there first, or use another device.',
      alreadyClaimed: 'This hub has just taken this device — the request was already used. Reopen the hub.',
      expired: 'The request expired before the hub could take it. Try again.',
      grantKeyMissing: 'The Torik cloud cannot sign the grant for this device right now. Try again in a moment.',
      unknownDevice: 'The hub does not know this enrollment. Try again.',
      notApproved: 'The hub has not recorded the grant for this device yet. Try again in a moment.',
      invalidProof: 'The hub refused this device’s key. Try again.',
      rateLimited: 'Too many attempts from this device. Wait a minute and try again.',
      unreachable: 'Nothing answered at this address. Check that the hub is on and on this network.',
      malformed: 'The hub answered without binding this device. Nothing was changed.',
      refused: 'The hub refused the request. Nothing was changed.',
      // The account path is closed for this person (card `t_6b11b40d`): the hub
      // has no user with this address, so the code surface is the way in.
      useCode: 'Use an enrollment code instead',
    },
    // A hub this app cannot open (card `t_bfe0edc9`): the sentence that says
    // whose credential is missing and where that person's own way in lives.
    // Read by `utils/hubOpenFallback.ts` through `t(plan.messageKey)`, on the
    // Local page, on the tab's own connect screen and by the connection-lost
    // overlay (`components/ConnectionLostOverlay.vue`, which shows what the
    // reconnect refused) — the overlay is a hub tab's own surface, so the
    // sentences live in all six catalogues, unlike `views.localServers.*`.
    open: {
      title: '{hub} could not be opened',
      dismiss: 'Dismiss',
      openLocalPage: 'Find local servers',
      openServers: 'Servers',
      // The hub's own pages were about to render their shell with no credential
      // behind them: the app states the fact instead of painting the shell.
      needsCredential:
        'This hub’s pages need a credential this device does not have. Nothing was sent and nothing was changed.',
      // One sentence per way a hub stays shut (`HUB_OPEN_FAILURES`). The key
      // segment is spelled the way every key path in this repo is spelled —
      // `[A-Za-z0-9_]` (`KEY_SHAPE`, `locales/localeAudit.ts`) — so a refusal
      // whose own name has hyphens (`no-credential`) becomes `no_credential`
      // here; `hubOpenMessageKey()` is the one place that translation happens.
      failure: {
        no_credential:
          'This device holds no credential for {hub} yet. Your Torik account opens it from here — no code and no password.',
        credential_refused:
          '{hub} did not accept the credential this device held, so it was dropped. Open it with your Torik account from here — no code is needed.',
        no_cloud_session: 'Sign in to your Torik account to open {hub}.',
        no_lan_address: '{hub} announced no address this device can dial.',
        no_account_identity:
          'Your Torik account does not hold {hub} yet. Adopt it here, or ask its owner to invite this address.',
        unreachable: 'Nothing answered at {address}. Check that {hub} is switched on and on this network.',
        permission_held:
          'The browser is holding this request: this site has not been allowed to reach your local network yet. Allow local network access for this site, then try again — or open {hub} over the cloud.',
        not_this_hub: 'Something answered at {address}, and it is not {hub}.',
        not_proven:
          '{hub} answered at {address} and could not prove it holds the key your account pinned. Nothing was sent.',
        cannot_prove: 'The device at {address} could not be asked to prove it is {hub}.',
        refused: '{hub} refused your Torik account’s claim — ask its owner to invite this address.',
        // The **cloud's** own refusal of the tunnel session
        // (`services/cloudSessionRefusal.ts`, plan FLOOR-PLAN-AND-PLAN-EDITOR.md
        // §3.2/D3): the hub's plan terms are local-only. Not a failure — the state
        // of the server, said once, with the one way to change it.
        remote_access_not_licensed:
          'This server is set to local access only — you can reach it from its own network. Remote access is included with a paid server plan.',
      },
      // The hub's own **local password** (plan §2/C5–C6, owner decision D1):
      // the door that needs no cloud, typed into the dialog the notice opens
      // (`components/server/LocalPasswordModal.vue`). `button` is the notice's
      // offer; the two failure sentences at the end are the ones the shared list
      // words for a *set* ("Enter a password."), which read as nonsense here.
      localPassword: {
        button: 'Open {hub} with the local password',
        title: 'Local password',
        intro:
          'The password set on {hub} itself. It works with the internet down — nothing here is sent to the Torik cloud.',
        identifier: 'Handle or email',
        password: 'Password',
        note: 'Kept for this session only — not saved on this device yet.',
        submit: 'Open {hub}',
        submitting: 'Opening…',
        cancel: 'Cancel',
        credentialsRequired: 'Enter your handle and password.',
        refused: 'The hub refused that sign-in.',
      },
    },
    // The **onboarding pass** (plan `LOCAL-SERVER-ONBOARDING-JOURNEY.md` §3/C2–C3,
    // owner decisions §7 Q1/Q2/Q4): what the Local page opens for a server this
    // account holds on a plan that keeps it on its own network. The state first,
    // then the honest paid-plan sentence, then the local password
    // (`hub.adopt.password.*` — the step the adopt sheet shares). Nothing here
    // sells: no price list and no purchase, the account holds the plan.
    onboard: {
      title: 'Set up this server',
      intro:
        'This server works on your own network. Here are its licence state and the password that keeps it reachable without the internet.',
      licenceLabel: 'Licence',
      reading: 'Reading…',
      remoteTitle: 'Remote access needs a paid plan',
      remoteBody: 'Remote access to this server is part of a paid plan. Manage the plan in your Torik account.',
      accountLink: 'Manage the plan in your account',
      accountHint: 'Opens your Torik account in the browser.',
      openButton: 'Set up {hub}',
      doneTitle: '{hub} is set up',
      doneSkipped: 'Done. You can set the local password any time from this page.',
      noAddress:
        'This server announced no address this device can dial, so the local password cannot be set from here. Open it from your servers first.',
    },
  },
  // WLED integration — the two tabs read these (WLEDSegmentsTab.vue, WLEDSettingsTab.vue)
  wled: {
    tabs: {
      segmentsTitle: 'Segments',
      segmentsSubtitle:
        'Each active WLED segment can be imported as a light device (effects, palettes and presets included).',
    },
    discover: 'Discover',
    discovering: 'Discovering…',
    creating: 'Creating…',
    createDevices: 'Create light devices',
    loading: 'Loading segments…',
    emptyTitle: 'No segments found',
    emptyMessage:
      'Press Discover to read the segment layout from the WLED board. Devices are created per segment — a default single-strip board has one segment covering all LEDs.',
    createHint: 'Create one light device per segment (default: all).',
    loadFailed: 'Failed to load segments',
    createFailed: 'Failed to create devices',
    discoveryFailed: 'Discovery failed',
    alreadyImported: 'Already-imported segments were skipped. New lights appear under Rooms / Devices.',
    colName: 'Segment',
    colType: 'Type',
    colColor: 'Color',
    colEffect: 'Effect',
    colPalette: 'Palette',
    colStatus: 'Status',
    online: 'Online',
    offline: 'Offline',
    settings: {
      title: 'Settings',
      subtitle: 'Connection settings for the WLED board. One integration manages one board.',
      connected: 'Connected',
      disconnected: 'Disconnected',
      noUrl: 'No board URL configured',
      enabled: 'Enabled',
      disabled: 'Disabled',
      reconnecting: 'Reconnecting…',
      reconnect: 'Reconnect',
      boardTitle: 'Board',
      refresh: 'Refresh',
      name: 'Name',
      version: 'Firmware',
      leds: 'LEDs',
      channels: 'Channels',
      url: 'Board URL',
      scanning: 'Scanning…',
      findOnNetwork: 'Find on network',
      urlHint: 'IP address or hostname of the WLED board — or use Find on network.',
      apiKey: 'API Key',
      apiKeyPlaceholder: 'Optional',
      hide: 'Hide',
      show: 'Show',
      apiKeyHint: 'Only needed when WLED security is enabled.',
      pollInterval: 'Poll interval (seconds)',
      pollHint: 'Fallback refresh used when the WebSocket is unavailable.',
      testing: 'Testing…',
      test: 'Test Connection',
      saved: 'Settings saved.',
      saving: 'Saving…',
      save: 'Save Settings',
      errorUpdate: 'Failed to update integration',
      errorReconnect: 'Reconnect failed',
      errorConnection: 'Connection failed',
      errorSave: 'Failed to save settings',
    },
  },

  // ── The panel miniapp: a panel surface running beside the app ───────────────
  // (POSITIONING.md, Consequence 4: a panel holds a device credential, not a user
  // account, so it keeps operating while the account half does not.)
  panelApp: {
    untitled: 'Panel',
    roleLabel: 'Role',
    runsBeside: 'runs beside your session',
    close: 'Close panel',
    pairingTitle: 'Connect this device to a panel',
    pairingHint: 'Open a panel from the panel list on a signed-in device, or type the pairing code it shows.',
    pinLabel: 'Pairing code',
    pinPlaceholder: 'e.g. 4K7-T2B',
    connect: 'Connect',
    connecting: 'Connecting…',
    pairingFailed: 'That pairing code was refused.',
    noPages: 'This panel has no pages yet.',
    credentialAccepted: 'Panel credential accepted',
    credentialRefused: 'Panel credential refused',
    credentialUnknown: 'Checking panel credential…',
    sessionNote: 'Your account stays signed in. This panel is a separate surface with its own credential.',
    openOnThisDevice: 'Open on this device',
    openOnThisDeviceHint: 'Shows this panel on this device, beside the app you are signed in to.',
    openFailed: 'Could not open this panel on this device.',
    deviceModeTitle: 'This device',
    deviceModeHint:
      'What this device is — not what it runs on. A panel takes the screen over on start; an app opens panels in a window of its own.',
    deviceModeApp: 'Use as an app',
    deviceModePanel: 'Use as a panel',
    deviceModeSaved: 'Saved for this device.',
  },
} as const;
